import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Truck,
  Search, 
  Filter,
  FileText, 
  Download, 
  Trash2, 
  Edit2,
  Edit,
  ChevronDown,
  Clock, 
  AlertCircle,
  Printer,
  Loader2,
  X,
  History,
  CreditCard,
  ArrowRight,
  Repeat,
  Upload,
  FileUp,
  XCircle,
  Send,
  CheckCircle2,
  Mail,
  MessageSquare,
  User,
  Folder,
  ChevronUp,
  Tag as TagIcon,
  Paperclip,
  Link as LinkIcon,
  QrCode
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getDefaultCurrency } from '../lib/currency';
import { CustomSelect } from './ui/CustomSelect';
import { SearchableSelect } from './ui/SearchableSelect';
import Papa from 'papaparse';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  arrayUnion,
  increment } from '../firebase';
import { db, auth, storage, ref, uploadBytes, getDownloadURL } from '../firebase';
import { DocumentPrintLayout } from './ui/DocumentPrintLayout';
import { StatusBadge } from './workflow/StatusBadge';
import type { StatusBadgeValue } from '../lib/documentStatus';
import { getDoc } from '../firebase';
import { logHistory } from '../services/historyService';
import { notify } from '../services/notify';
import DocumentChatterPanel from './DocumentChatterPanel';
import DiscussionsView from './DiscussionsView';
import { postBillJournal, postPaymentJournal } from '../services/accountingService';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import ConfirmModal from './ConfirmModal';
import ShareDocumentModal from './ShareDocumentModal';

interface ActionLogEntry {
  action: string;
  user: string;
  email: string;
  timestamp: string;
  details: string;
}

interface BillItem {
  item: string;
  description: string;
  quantity: number;
  receivedQuantity?: number;
  unitPrice: number;
  discount: number;
  discountType?: 'percentage' | 'amount';
  taxAmount: number;
  total: number;
  account: string;
  taxRate: string;
  trackingCategory?: string;
}

interface Bill {
  id: string;
  number: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  date: string;
  dueDate: string;
  reference: string;
  currency: string;
  brandingTheme: string;
  amountsAre: 'Tax Inclusive' | 'Tax Exclusive' | 'No Tax';
  items: BillItem[];
  totalAmount: number;
  status: 'Draft' | 'Awaiting Approval' | 'Awaiting Payment' | 'Paid' | 'Void' | 'Deleted';
  termsAndConditions: string;
  billingAddress?: string;
  deliveryAddress?: string;
  actionLog?: ActionLogEntry[];
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
  actionDetails?: string;
  source?: string;
  tags?: string[];
  purchaseOrderId?: string;
  paymentReceived?: number;
  attachments?: { name: string, url: string, type: string, size: number, createdAt: string }[];
  type: 'bill' | 'repeating_bill' | 'credit_note';
  threads?: any[];
  time?: string;
  notes?: string;
  repeatingConfig?: {
    frequency: number;
    frequencyUnit: 'Week' | 'Month';
    startDate: string;
    endDate?: string;
    dueDateDays: number;
    dueDateType: 'daysAfterBillDate' | 'dayOfFollowingMonth' | 'dayOfCurrentMonth' | 'daysAfterEndOfBillMonth';
    approveStatus: 'Draft' | 'Approved';
    lastGeneratedDate?: string;
  };
}

interface Contact {
  id: string;
  name: string;
  email: string;
  emails?: string[];
  type?: 'Customer' | 'Supplier' | 'Both';
  address?: string;
  billingAddress?: string;
  deliveryAddress?: string;
  purchaseAccount?: string;
  purchaseTaxRate?: string;
  purchasePaymentTerms?: string;
  currency?: string;
  purchaseCurrency?: string;
}

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  enablePayments?: boolean;
}

// Error Info Interface for Firestore
enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: any;
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
}

const hasLocalSession = () => typeof window !== 'undefined' && Boolean(localStorage.getItem('app_auth_token'));

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('app_auth_token');
  if (!token) throw new Error('Local session token is unavailable');

  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    throw new Error((await response.text()) || `Request failed (${response.status})`);
  }

  return response.status === 204 ? (undefined as T) : response.json();
};

const localApiRecords = <T,>(payload: any): T[] =>
  Array.isArray(payload) ? payload : payload?.documents || payload?.contacts || payload?.items ||
  payload?.accounts || payload?.chartOfAccounts || payload?.taxRates || payload?.tax_rates ||
  payload?.bankAccounts || payload?.bank_accounts || payload?.data || [];

const localApiRecord = <T,>(payload: any): T =>
  (payload?.document || payload?.data || payload) as T;

const billBadgeStatus = (status: Bill['status']): StatusBadgeValue => {
  if (status === 'Draft') return 'draft';
  if (status === 'Awaiting Approval') return 'pending';
  if (status === 'Paid') return 'paid';
  if (status === 'Void' || status === 'Deleted') return 'cancelled';
  return 'done';
};

const isBillValueLocked = (status: Bill['status']) =>
  ['Awaiting Payment', 'Paid', 'Void', 'Deleted'].includes(status);

export default function BillsView({ orgId, orgSettings: providedOrgSettings, onNavigate , language = 'en', initialBillId, onClearInitial } : { language?: string,  orgId: string, orgSettings?: any, onNavigate?: (tab: string, id?: string) => void, initialBillId?: string | null, onClearInitial?: () => void }) {
  const [bills, setBills] = useState<Bill[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [docToPrint, setDocToPrint] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);

  useEffect(() => {
    if (!orgId || hasLocalSession()) return;
    const fetchOrg = async () => {
      const docSnap = await getDoc(doc(db, 'organizations', orgId));
      if (docSnap.exists()) {
        setOrganization({ id: docSnap.id, ...docSnap.data() });
      }
    };
    fetchOrg();
  }, [orgId]);

  const handlePrint = (doc: any) => {
    setDocToPrint(doc);
    setIsPrintModalOpen(true);
  };
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [activeDeliveryNote, setActiveDeliveryNote] = useState<any | null>(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const [shareMode, setShareMode] = useState<'link' | 'qr' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [refFilter, setRefFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const [showThread, setShowThread] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const billFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasLocalSession()) {
      if (!e.target.files?.length || !selectedBill) return;
      const file = e.target.files[0];

      if (file.size > 20 * 1024 * 1024) {
        notify.error("File is too large. Max size is 20MB");
        e.target.value = '';
        return;
      }

      setIsUploading(true);
      setUploadProgress(10);

      try {
        const token = localStorage.getItem('app_auth_token');
        if (!token) throw new Error('Local session token is unavailable');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('targetPath', `bill_attachments/${selectedBill.id}/${Date.now()}_${file.name}`);

        const uploadResponse = await fetch('/api/files/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!uploadResponse.ok) throw new Error('Could not upload file');

        const uploadResult = await uploadResponse.json();
        const uploadedFile = uploadResult.file || uploadResult;
        const url = uploadedFile.url || uploadedFile.downloadUrl || uploadedFile.downloadURL;
        if (!url) throw new Error('Upload did not return a file URL');

        const attachment = {
          ...(uploadedFile.id ? { id: uploadedFile.id } : {}),
          name: uploadedFile.name || file.name,
          url,
          type: uploadedFile.type || file.type || 'application/octet-stream',
          size: uploadedFile.size || file.size,
          createdAt: uploadedFile.createdAt || new Date().toISOString(),
        };
        const attachments = [...(selectedBill.attachments || []), attachment];
        const documentResponse = await fetch(`/api/organizations/${orgId}/documents/${selectedBill.id}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ attachments }),
        });
        if (!documentResponse.ok) throw new Error('Could not attach file to bill');

        const updatedBill = localApiRecord<Bill>(await documentResponse.json());
        setSelectedBill(updatedBill);
        setBills(current => current.map(bill => bill.id === updatedBill.id ? updatedBill : bill));
        notify.confirm(`File "${file.name}" attached successfully`);
      } catch (error: any) {
        console.error('Local upload error:', error);
        notify.error(`Failed to upload file: ${error.message || 'Unknown error'}`);
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        if (e.target) e.target.value = '';
      }
      return;
    }
    if (!e.target.files?.length || !selectedBill) return;
    const file = e.target.files[0];
    
    if (file.size > 20 * 1024 * 1024) {
      notify.error("File is too large. Max size is 20MB");
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      const storagePath = `bill_attachments/${selectedBill.id}/${Date.now()}_${file.name}`;
      const fileRef = ref(storage, storagePath);
      
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      const attachment = {
        name: file.name,
        url: downloadURL,
        type: file.type || 'application/octet-stream',
        size: file.size,
        createdAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'documents', selectedBill.id), {
        attachments: arrayUnion(attachment),
        actionLog: arrayUnion({
          action: 'File Attached',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || '',
          timestamp: new Date().toISOString(),
          details: `Attached file: ${file.name}`
        })
      });
      
      setSelectedBill(prev => prev ? {
        ...prev,
        attachments: [...(prev.attachments || []), attachment]
      } as Bill : null);
      
      notify.confirm(`File "${file.name}" attached successfully`);
    } catch (error: any) {
      console.error("Upload error:", error);
      notify.error(`Failed to upload file: ${error.message || 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveAttachment = async (attachmentUrl: string) => {
    if (hasLocalSession()) {
      if (!selectedBill) return;

      try {
        const token = localStorage.getItem('app_auth_token');
        if (!token) throw new Error('Local session token is unavailable');

        const attachments = selectedBill.attachments?.filter(attachment => attachment.url !== attachmentUrl) || [];
        const response = await fetch(`/api/organizations/${orgId}/documents/${selectedBill.id}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ attachments }),
        });
        if (!response.ok) throw new Error('Could not remove attachment');

        const updatedBill = localApiRecord<Bill>(await response.json());
        setSelectedBill(updatedBill);
        setBills(current => current.map(bill => bill.id === updatedBill.id ? updatedBill : bill));
        notify.confirm("Attachment removed");
      } catch (error) {
        console.error('Failed to remove local attachment:', error);
        notify.error("Failed to remove attachment");
      }
      return;
    }
    if (!selectedBill) return;
    
    try {
      const updatedAttachments = selectedBill.attachments?.filter(a => a.url !== attachmentUrl) || [];
      await updateDoc(doc(db, 'documents', selectedBill.id), {
        attachments: updatedAttachments,
        actionLog: arrayUnion({
          action: 'Attachment Removed',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || '',
          timestamp: new Date().toISOString(),
          details: `Removed an attachment`
        })
      });
      
      setSelectedBill(prev => prev ? {
        ...prev,
        attachments: updatedAttachments
      } : null);
      
      notify.confirm("Attachment removed");
    } catch (error) {
      console.error("Error removing attachment:", error);
      notify.error("Failed to remove attachment");
    }
  };

  const isOverdue = (dueDate: string, status: string) => {
    if (status === 'Paid' || status === 'Void' || status === 'Deleted') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const getTaxRateValue = (rateName: string) => {
    if (!rateName) return 0;
    if (rateName.endsWith('%')) {
      return parseFloat(rateName) || 0;
    }
    const rateObj = taxRates.find(r => r.name === rateName);
    return rateObj ? parseFloat(rateObj.rate) || 0 : 0;
  };

  // Discussion functions replaced by DiscussionsView

  const updateLocalBillStatus = async (bill: Bill, status: Bill['status']) => {
    const response = await localApiRequest(`/api/organizations/${orgId}/documents/${bill.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    const updatedBill = { ...bill, ...localApiRecord<Bill>(response), status } as Bill;
    setBills(current => current.map(currentBill => currentBill.id === bill.id ? updatedBill : currentBill));
    setSelectedBill(current => current?.id === bill.id ? updatedBill : current);
    if (status === 'Awaiting Payment') notify.confirm('Journal posting is server-side in local mode.');
  };

  const handleGenerateRecurringBill = async (template: Bill) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      if (hasLocalSession()) {
        const response = await localApiRequest<any>(
          `/api/organizations/${orgId}/documents/${template.id}/generate-recurring`,
          { method: 'POST' }
        );
        const generatedBill = (
          response?.generatedDocument ||
          response?.generatedBill ||
          response?.document ||
          response?.data?.document ||
          response?.data ||
          response
        ) as Bill;
        const updatedTemplate = (
          response?.template ||
          response?.updatedTemplate ||
          response?.recurringTemplate ||
          response?.data?.template
        ) as Bill | undefined;

        if (!generatedBill?.id) {
          throw new Error('Recurring bill generation did not return a document');
        }

        setBills(current => {
          const withUpdatedTemplate = updatedTemplate?.id
            ? current.map(bill => bill.id === updatedTemplate.id ? { ...bill, ...updatedTemplate } : bill)
            : current;
          return [generatedBill, ...withUpdatedTemplate.filter(bill => bill.id !== generatedBill.id)];
        });
        setSelectedBill(generatedBill);
      } else {
        if (!auth.currentUser) return;

        const today = new Date();
        const billDate = today.toISOString().split('T')[0];
        const config = template.repeatingConfig;
        const dueDate = new Date(today);
        const dueDateDays = config?.dueDateDays || 0;

        switch (config?.dueDateType) {
          case 'dayOfFollowingMonth':
            dueDate.setMonth(dueDate.getMonth() + 1, dueDateDays);
            break;
          case 'dayOfCurrentMonth':
            dueDate.setDate(dueDateDays);
            break;
          case 'daysAfterEndOfBillMonth':
            dueDate.setMonth(dueDate.getMonth() + 1, dueDateDays);
            break;
          default:
            dueDate.setDate(dueDate.getDate() + dueDateDays);
        }

        const generatedStatus: Bill['status'] = config?.approveStatus === 'Approved'
          ? 'Awaiting Payment'
          : 'Draft';
        const { id, repeatingConfig, createdAt, updatedAt, actionLog, ...templateData } = template;
        const generatedBill = {
          ...templateData,
          number: `BILL-${orgSettings?.nextBillNumber || 1000}`,
          date: billDate,
          dueDate: dueDate.toISOString().split('T')[0],
          type: 'bill' as const,
          status: generatedStatus,
          source: template.id,
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Unknown User',
          createdByEmail: auth.currentUser.email || 'No Email',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          actionDetails: `Generated from recurring bill ${template.number}`,
          actionLog: [{
            action: 'Generated',
            user: auth.currentUser.displayName || 'Unknown User',
            email: auth.currentUser.email || 'No Email',
            timestamp: new Date().toISOString(),
            details: `Generated from recurring bill ${template.number}`,
          }],
        };
        const generatedDoc = await addDoc(collection(db, 'documents'), generatedBill);

        await updateDoc(doc(db, 'documents', template.id), {
          'repeatingConfig.lastGeneratedDate': billDate,
          updatedAt: serverTimestamp(),
        });
        if (generatedStatus === 'Awaiting Payment') {
          await postBillJournal(orgId, { ...generatedBill, id: generatedDoc.id }, auth.currentUser.uid);
        }
        await updateDoc(doc(db, 'settings', orgId), { nextBillNumber: increment(1) });
      }

      notify.confirm('Recurring bill generated');
    } catch (error) {
      console.error('Error generating recurring bill:', error);
      notify.error('Failed to generate recurring bill');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkAction = async (action: 'submit' | 'approve' | 'delete' | 'print' | 'email') => {
    if (selectedBills.length === 0) return;

    try {
      if (hasLocalSession() && ['delete', 'approve', 'submit'].includes(action)) {
        const status: Bill['status'] = action === 'delete' ? 'Void' : action === 'approve' ? 'Awaiting Payment' : 'Awaiting Approval';
        await Promise.all(selectedBills.map(id =>
          localApiRequest(`/api/organizations/${orgId}/documents/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
          })
        ));
        setBills(current => current.map(bill => selectedBills.includes(bill.id) ? { ...bill, status } as Bill : bill));
        setSelectedBill(current => current && selectedBills.includes(current.id) ? { ...current, status } as Bill : current);
        if (action === 'approve') notify.confirm('Journal posting is server-side in local mode.');
        notify.confirm(action === 'delete' ? `${selectedBills.length} bills voided` : action === 'approve' ? `${selectedBills.length} bills approved` : `${selectedBills.length} bills submitted for approval`);
        setSelectedBills([]);
        return;
      }
      if (action === 'delete') {
        for (const id of selectedBills) {
          await updateDoc(doc(db, 'documents', id), { status: 'Void' });
        }
        notify.confirm(`${selectedBills.length} bills voided`);
      } else if (action === 'approve') {
        for (const id of selectedBills) {
          await updateDoc(doc(db, 'documents', id), { status: 'Awaiting Payment' });
          const bill = bills.find(b => b.id === id);
          if (bill) {
            await postBillJournal(orgId, bill, auth.currentUser?.uid || 'system');
          }
        }
        notify.confirm(`${selectedBills.length} bills approved`);
      } else if (action === 'submit') {
        for (const id of selectedBills) {
          await updateDoc(doc(db, 'documents', id), { status: 'Awaiting Approval' });
        }
        notify.confirm(`${selectedBills.length} bills submitted for approval`);
      } else if (action === 'print') {
        notify.confirm("Printing functionality for multiple bills is being prepared.");
      } else if (action === 'email') {
        notify.confirm("Email functionality for multiple bills is being prepared.");
      }
      
      setSelectedBills([]);
    } catch (error) {
      console.error("Error performing bulk action:", error);
      notify.error("Failed to perform bulk action");
    }
  };
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [billPayments, setBillPayments] = useState<any[]>([]);
  const [paymentToDelete, setPaymentToDelete] = useState<any>(null);
  const [paymentData, setPaymentData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    accountId: '',
    method: 'Bank Transfer',
    reference: ''
  });

  // Repeating Bill Modal state
  const [isRepeatingModalOpen, setIsRepeatingModalOpen] = useState(false);
  const [repeatingConfig, setRepeatingConfig] = useState({
    frequency: 1,
    frequencyUnit: 'Month' as 'Week' | 'Month',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    dueDateDays: 7,
    dueDateType: 'daysAfterBillDate' as 'daysAfterBillDate' | 'dayOfFollowingMonth' | 'dayOfCurrentMonth' | 'daysAfterEndOfBillMonth',
    approveStatus: 'Draft' as 'Draft' | 'Approved'
  });

  // Credit Note Modal state
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);

  // Import Modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [trackingCategories, setTrackingCategories] = useState<any[]>([]);
  const [brandingThemes, setBrandingThemes] = useState<any[]>([]);

  const [newBill, setNewBill] = useState<Omit<Bill, 'id'> & { trackingCategory?: string; contactEmail?: string }>({
    number: '',
    contactId: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reference: '',
    currency: getDefaultCurrency(providedOrgSettings),
    brandingTheme: 'Standard',
    amountsAre: 'Tax Exclusive',
    items: [{ 
      item: '', 
      description: '', 
      quantity: 1, 
      unitPrice: 0,
      discount: 0,
      taxAmount: 0,
      total: 0,
      account: '',
      taxRate: '0%',
      trackingCategory: ''
    }],
    totalAmount: 0,
    status: 'Draft',
    termsAndConditions: '',
    billingAddress: '',
    deliveryAddress: '',
    type: 'bill',
    trackingCategory: '',
    tags: []
  });

  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');

  const [currencies, setCurrencies] = useState(['EGP', 'USD', 'EUR', 'GBP', 'AUD', 'CAD']);
  const [orgSettings, setOrgSettings] = useState<any>(providedOrgSettings || null);
  const organizationCurrency = getDefaultCurrency(orgSettings);

  useEffect(() => {
    if (providedOrgSettings) setOrgSettings(providedOrgSettings);
  }, [providedOrgSettings]);
  const [itemsList, setItemsList] = useState<any[]>([]);

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-bill') {
        setIsEditing(false);
        setNewBill({
          number: '',
          contactId: '',
          contactName: '',
          contactEmail: '',
          date: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          reference: '',
          currency: organizationCurrency,
          brandingTheme: 'Standard',
          amountsAre: 'Tax Exclusive',
          items: [],
          status: 'Draft',
          totalAmount: 0,
          type: 'bill',
          termsAndConditions: ''
        });
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, [organizationCurrency]);

  useEffect(() => {
    if (!orgId) return;

    if (hasLocalSession()) {
      let cancelled = false;
      const loadLocalBillData = async () => {
        setIsLoading(true);
        try {
          const [
            documentsResponse, contactsResponse, itemsResponse, accountsResponse, taxRatesResponse,
            bankAccountsResponse, settingsResponse, tagsResponse, brandingThemesResponse, trackingCategoriesResponse,
          ] = await Promise.all([
            localApiRequest(`/api/organizations/${orgId}/documents?types=bill,repeating_bill,credit_note`),
            localApiRequest(`/api/organizations/${orgId}/contacts`),
            localApiRequest(`/api/organizations/${orgId}/items`),
            localApiRequest(`/api/organizations/${orgId}/chart-of-accounts`),
            localApiRequest(`/api/organizations/${orgId}/tax-rates`),
            localApiRequest(`/api/organizations/${orgId}/bank-accounts`),
            localApiRequest(`/api/organizations/${orgId}/settings`),
            localApiRequest(`/api/organizations/${orgId}/tags`),
            localApiRequest(`/api/organizations/${orgId}/branding-themes`),
            localApiRequest(`/api/organizations/${orgId}/tracking-categories?status=active`),
          ]);
          if (cancelled) return;

          setBills(localApiRecords<Bill>(documentsResponse));
          setContacts(localApiRecords<Contact>(contactsResponse).filter(contact => !contact.type || contact.type === 'Supplier' || contact.type === 'Both'));
          setItemsList(localApiRecords<any>(itemsResponse));
          setAccounts(localApiRecords<Account>(accountsResponse));
          setTaxRates(localApiRecords<any>(taxRatesResponse));
          setBankAccounts(localApiRecords<any>(bankAccountsResponse));
          const localSettings = localApiRecord<any>(settingsResponse) || {};
          const normalizedSettings = {
            ...localSettings,
            defaultCurrency: getDefaultCurrency(localSettings),
            allowedCurrencies: localSettings.allowedCurrencies ?? localSettings.allowed_currencies ?? [],
            nextBillNumber: localSettings.nextBillNumber ?? localSettings.next_purchase_bill_number ?? 1000,
          };
          setOrgSettings(normalizedSettings);
          const allowedCurrencies = normalizedSettings.allowedCurrencies;
          if (Array.isArray(allowedCurrencies) && allowedCurrencies.length > 0) {
            setCurrencies([...new Set([normalizedSettings.defaultCurrency, ...allowedCurrencies])]);
          } else {
            setCurrencies([normalizedSettings.defaultCurrency]);
          }
          if (!isEditing) {
            setNewBill(current => ({
              ...current,
              number: `BILL-${normalizedSettings.nextBillNumber}`,
              currency: normalizedSettings.defaultCurrency,
            }));
          }
          setAvailableTags(localApiRecords<any>(tagsResponse).filter(tag => !tag.type || tag.type === 'purchases'));
          setBrandingThemes(localApiRecords<any>(brandingThemesResponse));
          setTrackingCategories(localApiRecords<any>(trackingCategoriesResponse));
        } catch (error) {
          console.error('Local bill data load failed:', error);
          notify.error('Failed to load bills');
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      loadLocalBillData();
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'documents'), 
      where('type', 'in', ['bill', 'repeating_bill', 'credit_note']),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Bill[];
      setBills(data);
      setIsLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'documents');
      setIsLoading(false);
    });

    const contactsQ = query(
      collection(db, 'contacts'), 
      where('orgId', '==', orgId),
      where('type', 'in', ['Supplier', 'Both'])
    );
    const unsubContacts = onSnapshot(contactsQ, (snapshot) => {
      setContacts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Contact)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'contacts');
    });

    const accountsQ = query(
      collection(db, 'chart_of_accounts'), 
      where('orgId', '==', orgId),
      orderBy('code', 'asc')
    );
    const unsubAccounts = onSnapshot(accountsQ, (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts');
    });

    const bankAccountsQ = query(
      collection(db, 'bank_accounts'), 
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    const unsubBankAccounts = onSnapshot(bankAccountsQ, (snapshot) => {
      setBankAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'bank_accounts');
    });

    const itemsQ = query(
      collection(db, 'items'), 
      where('orgId', '==', orgId),
      orderBy('name', 'asc')
    );
    const unsubItems = onSnapshot(itemsQ, (snapshot) => {
      setItemsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'items');
    });

    // Fetch next Bill number
    const unsubSettings = onSnapshot(doc(db, 'settings', orgId), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setOrgSettings(data);
        
        if (data.allowedCurrencies && data.allowedCurrencies.length > 0) {
          const allCurrencies = [...new Set([getDefaultCurrency(data), ...data.allowedCurrencies])];
          setCurrencies(allCurrencies);
        } else if (data.defaultCurrency) {
          setCurrencies([data.defaultCurrency]);
        }

        if (!isEditing) {
          const nextNum = data.nextBillNumber || 1000;
          setNewBill(prev => ({ 
            ...prev, 
            number: `BILL-${nextNum}`,
            currency: getDefaultCurrency(data)
          }));
        }
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, `settings/${orgId}`);
    });

    const trackingQ = query(
      collection(db, 'tracking_categories'), 
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubTracking = onSnapshot(trackingQ, (snapshot) => {
      setTrackingCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tracking_categories');
    });

    const themesQ = query(
      collection(db, 'branding_themes'),
      where('orgId', '==', orgId)
    );
    const unsubThemes = onSnapshot(themesQ, (snapshot) => {
      setBrandingThemes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'branding_themes');
    });

    const taxRatesQ = query(
      collection(db, 'tax_rates'),
      where('orgId', '==', orgId)
    );
    const unsubTaxRates = onSnapshot(taxRatesQ, (snapshot) => {
      setTaxRates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tax_rates');
    });

    // Fetch Tags
    const tagsQ = query(
      collection(db, 'tags'),
      where('orgId', '==', orgId),
      where('type', 'in', ['purchases', null]),
      orderBy('createdAt', 'desc')
    );
    const unsubTags = onSnapshot(tagsQ, (snapshot) => {
      setAvailableTags(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tags');
    });

    return () => {
      unsubscribe();
      unsubContacts();
      unsubAccounts();
      unsubBankAccounts();
      unsubItems();
      unsubSettings();
      unsubTracking();
      unsubThemes();
      unsubTaxRates();
      unsubTags();
    };
  }, [orgId, isEditing]);

  useEffect(() => {
    if (initialBillId) {
      if (initialBillId.startsWith('new-bill-for:')) {
        const contactId = initialBillId.split(':')[1];
        setIsEditing(false);
        setNewBill({
          number: '',
          contactId: contactId,
          date: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          reference: '',
          currency: organizationCurrency,
          brandingTheme: 'Standard',
          amountsAre: 'Tax Exclusive',
          type: 'bill',
          items: [],
          status: 'Draft',
          totalAmount: 0,
          attachments: [],
          notes: '',
          termsAndConditions: '',
          tags: []
        });
        setIsModalOpen(true);
        if (onClearInitial) onClearInitial();
      } else if (bills.length > 0) {
        const bill = bills.find(b => b.id === initialBillId);
        if (bill) {
          setSelectedBill(bill);
          if (onClearInitial) onClearInitial();
        }
      }
    }
  }, [initialBillId, bills]);

  useEffect(() => {
    if (!selectedBill || !orgId) return;
    if (hasLocalSession()) {
      let cancelled = false;
      const loadLocalPayments = async () => {
        try {
          const response = await localApiRequest(`/api/organizations/${orgId}/documents/${selectedBill.id}/payments`);
          if (!cancelled) setBillPayments(localApiRecords<any>(response));
        } catch (error) {
          console.error('Local bill payment history load failed:', error);
          if (!cancelled) notify.error('Failed to load payment history');
        }
      };

      loadLocalPayments();
      return () => { cancelled = true; };
    }
    
    const q = query(
      collection(db, 'transactions'), 
      where('invoiceId', '==', selectedBill.id),
      where('orgId', '==', orgId),
      orderBy('date', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBillPayments(payments);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'transactions');
    });
    
    return () => unsubscribe();
  }, [selectedBill, orgId]);

  const calculateTotals = (items: BillItem[]) => {
    return items.reduce((acc, item) => {
      const subtotal = item.quantity * item.unitPrice;
      const discountAmount = item.discountType === 'amount'
        ? item.discount
        : (subtotal * item.discount) / 100;
      const taxableAmount = subtotal - discountAmount;
      const taxRateValue = getTaxRateValue(item.taxRate) / 100;
      const taxAmount = taxableAmount * taxRateValue;
      return acc + taxableAmount + taxAmount;
    }, 0);
  };

  const handleAddItem = () => {
    setNewBill({
      ...newBill,
      items: [...newBill.items, { 
        item: '', 
        description: '', 
        quantity: 1, 
        unitPrice: 0,
        discount: 0,
        discountType: 'percentage',
        taxAmount: 0,
        total: 0,
        account: '',
        taxRate: '0%'
      }]
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = newBill.items.filter((_, i) => i !== index);
    setNewBill({ ...newBill, items: newItems });
  };

  const handleItemChange = (index: number, field: keyof BillItem | Record<string, any>, value?: any) => {
    const newItems = [...newBill.items];
    
    if (typeof field === 'object') {
      newItems[index] = { ...newItems[index], ...field };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    
    // Recalculate item total
    const item = newItems[index];
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount = item.discountType === 'amount'
      ? item.discount
      : (subtotal * (item.discount || 0)) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxRateValue = getTaxRateValue(item.taxRate || '0') / 100;
    const taxAmount = taxableAmount * taxRateValue;
    newItems[index].total = taxableAmount + taxAmount;
    newItems[index].taxAmount = taxAmount;

    setNewBill(prev => ({ ...prev, items: newItems, totalAmount: calculateTotals(newItems) }));
  };

  const handleContactChange = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setNewBill(prev => ({
        ...prev,
        contactId,
        contactName: contact.name || '',
        contactEmail: contact.email || '',
        currency: contact.purchaseCurrency || contact.currency || prev.currency,
        billingAddress: contact.billingAddress || contact.address || '',
        deliveryAddress: contact.deliveryAddress || contact.address || '',
        termsAndConditions: contact.purchasePaymentTerms ? `Payment Terms: ${contact.purchasePaymentTerms}` : prev.termsAndConditions,
        items: prev.items.map((item, index) => index === 0 ? {
          ...item,
          account: contact.purchaseAccount || item.account,
          taxRate: contact.purchaseTaxRate || item.taxRate
        } : item)
      }));
    } else {
      setNewBill(prev => ({ ...prev, contactId }));
    }
  };

  const handleSendEmail = () => {
    if (!selectedBill) return;
    const contact = contacts.find(c => c.id === selectedBill.contactId) || 
                   contacts.find(c => c.name === selectedBill.contactName);
    const email = contact?.email || 
                 (contact?.emails && contact.emails.length > 0 ? contact.emails[0] : null) || 
                 (selectedBill as any).contactEmail;
    
    if (!email) {
      notify.error(language === 'ar' ? "لا يوجد بريد إلكتروني للمورد" : "No email for supplier");
      return;
    }

    const itemsList = selectedBill.items.map(item => {
      const subtotal = item.quantity * item.unitPrice;
      const discountAmount = (subtotal * (item.discount || 0)) / 100;
      const itemTotal = subtotal - discountAmount;
      return `• ${item.description || item.item} (${item.quantity} x ${selectedBill.currency} ${(item.unitPrice || 0).toLocaleString()}${item.discount ? `, Disc: ${item.discount}%` : ''}): ${selectedBill.currency} ${(itemTotal || 0).toLocaleString()}`;
    }).join('\n');

    const subject = `Bill ${selectedBill.number} from Accountex`;
    const body = `Dear ${contact?.name || selectedBill.contactName || 'Supplier'},

I am writing regarding Bill ${selectedBill.number}.

Bill Date: ${new Date(selectedBill.date).toLocaleDateString()}
Due Date: ${new Date(selectedBill.dueDate).toLocaleDateString()}
${selectedBill.reference ? `Reference: ${selectedBill.reference}` : ''}

Items Summary:
${itemsList}

Total Amount Payable: ${selectedBill.currency} ${selectedBill.totalAmount.toLocaleString()}

Please let us know if you require any further information.

Best regards,
Accountex Team`;
    
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
  };

  const handleSaveDeliveryNote = async () => {
    if (hasLocalSession()) {
      notify.confirm('Delivery and inventory flows are server-side in local mode.');
      return;
    }
    if (!activeDeliveryNote || !orgId) return;
    
    // Validation: Check if quantities to receive are valid
    const hasValidQtys = (activeDeliveryNote?.items || []).some((item: any) => (item.qtyToShipNow || 0) > 0);
    if (!hasValidQtys) {
      notify.error("Please specify at least one quantity to receive.");
      return;
    }

    setIsSaving(true);
    try {
      const deliveryNoteData: any = {
        invoiceId: activeDeliveryNote.invoiceId, // Actually the billId
        invoiceNumber: activeDeliveryNote.invoiceNumber, // Actually billNumber
        orgId,
        contactId: activeDeliveryNote.contactId,
        contactName: activeDeliveryNote.contactName,
        items: (activeDeliveryNote.items || []).map((item: any) => ({
          itemId: item.itemId,
          itemCode: item.itemCode,
          name: item.name,
          description: item.description,
          quantityOrdered: item.quantityOrdered,
          quantityShipped: item.qtyToShipNow || 0,
          lotNumber: item.lotNumber || '',
          expiryDate: item.expiryDate || ''
        })).filter((i: any) => i.quantityShipped > 0),
        assignedPersonnelId: activeDeliveryNote.assignedPersonnelId || '',
        assignedPersonnelName: activeDeliveryNote.assignedPersonnelName || '',
        warehouseId: activeDeliveryNote.warehouseId || '',
        warehouseName: activeDeliveryNote.warehouseName || '',
        status: activeDeliveryNote.assignedPersonnelId ? 'Assigned' : 'Draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (activeDeliveryNote.id) {
        await updateDoc(doc(db, 'purchase_delivery_notes', activeDeliveryNote.id), deliveryNoteData);
        await logHistory(orgId, 'Updated Goods Receipt', `Receipt #${activeDeliveryNote.id.slice(-6).toUpperCase()} updated.`, auth.currentUser, activeDeliveryNote.id, 'PurchaseDelivery');
        notify.confirm('Goods Receipt updated');
      } else {
        const docRef = await addDoc(collection(db, 'purchase_delivery_notes'), deliveryNoteData);
        await logHistory(orgId, 'Created Goods Receipt', `Receipt #${docRef.id.slice(-6).toUpperCase()} created from Bill ${activeDeliveryNote.invoiceNumber}.`, auth.currentUser, docRef.id, 'PurchaseDelivery');
        
        // Log to bill action log
        await updateDoc(doc(db, 'documents', activeDeliveryNote.invoiceId), {
          actionLog: arrayUnion({
            action: 'Goods Receipt Created',
            user: auth.currentUser?.displayName || 'Unknown User',
            email: auth.currentUser?.email || '',
            timestamp: new Date().toISOString(),
            details: `Goods Receipt #${docRef.id.slice(-6).toUpperCase()} created with ${deliveryNoteData.items.length} items.`,
            deliveryNoteId: docRef.id
          })
        });
        notify.confirm('Goods Receipt created successfully');
      }

      setIsDeliveryModalOpen(false);
      setActiveDeliveryNote(null);
    } catch (error) {
      console.error(error);
      notify.error('Error creating Goods Receipt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBill = async (e: React.FormEvent, statusOverride?: Bill['status']) => {
    if (e) e.preventDefault();
    if (isEditing && selectedBill && isBillValueLocked(selectedBill.status)) {
      notify.error(language === 'ar' ? 'لا يمكن تعديل قيم فاتورة مكتملة' : 'Completed bill values are locked');
      return;
    }
    const isLocal = hasLocalSession();
    if (!isLocal && !auth.currentUser) return;

    setIsSaving(true);
    const contact = contacts.find(c => c.id === newBill.contactId);
    const contactName = contact?.name || newBill.contactName || '';
    const contactEmail = contact?.email || (newBill as any).contactEmail || '';
    const totalAmount = calculateTotals(newBill.items);
    
    const finalStatus = statusOverride || newBill.status;

    const actionDetails = isEditing 
      ? `Bill ${newBill.number} updated by ${auth.currentUser?.displayName || 'Unknown User'}`
      : `Bill ${newBill.number} created from ${contact?.name || 'Unknown Vendor'}`;

    const actionEntry: ActionLogEntry = {
      action: isEditing ? 'Edited' : (finalStatus === 'Awaiting Approval' ? 'Submitted for Approval' : 'Created'),
      user: auth.currentUser?.displayName || 'Unknown User',
      email: auth.currentUser?.email || 'No Email',
      timestamp: new Date().toISOString(),
      details: actionDetails
    };

    try {
      if (isLocal) {
        const localDocument = {
          ...newBill,
          status: isRepeatingModalOpen ? 'Draft' : finalStatus,
          type: isCreditNoteModalOpen ? 'credit_note' : (isRepeatingModalOpen ? 'repeating_bill' : 'bill'),
          contactName,
          contactEmail,
          totalAmount,
          actionDetails,
          ...(isRepeatingModalOpen ? { repeatingConfig } : {}),
        };
        const response = isEditing && selectedBill
          ? await localApiRequest(`/api/organizations/${orgId}/documents/${selectedBill.id}`, {
              method: 'PATCH',
              body: JSON.stringify(localDocument),
            })
          : await localApiRequest(`/api/organizations/${orgId}/documents`, {
              method: 'POST',
              body: JSON.stringify(localDocument),
            });
        const savedBill = localApiRecord<Bill>(response);
        const billWithId = { ...localDocument, ...savedBill, id: savedBill.id || selectedBill?.id } as Bill;
        setBills(current => isEditing
          ? current.map(bill => bill.id === billWithId.id ? billWithId : bill)
          : [billWithId, ...current]);
        setSelectedBill(current => current?.id === billWithId.id ? billWithId : current);
        if (finalStatus === 'Awaiting Payment') notify.confirm('Journal posting is server-side in local mode.');
      } else if (isEditing && selectedBill) {
        await updateDoc(doc(db, 'documents', selectedBill.id), {
          ...newBill,
          status: finalStatus,
          contactName,
          contactEmail,
          totalAmount,
          actionDetails,
          updatedAt: serverTimestamp(),
          actionLog: arrayUnion(actionEntry)
        });
        await logHistory(orgId, 'Updated Bill', `Bill ${newBill.number} updated`, auth.currentUser, selectedBill.id, 'Bill');
      } else {
        const docData: any = {
          ...newBill,
          status: finalStatus,
          type: isCreditNoteModalOpen ? 'credit_note' : (isRepeatingModalOpen ? 'repeating_bill' : 'bill'),
          contactName,
          contactEmail,
          totalAmount,
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Unknown User',
          createdByEmail: auth.currentUser.email || 'No Email',
          orgId,
          actionDetails,
          createdAt: serverTimestamp(),
          actionLog: [actionEntry]
        };

        if (isRepeatingModalOpen) {
          docData.repeatingConfig = repeatingConfig;
          docData.status = 'Draft'; // Repeating bills are templates
        }

        const docRef = await addDoc(collection(db, 'documents'), docData);
        await logHistory(orgId, 'Created Bill', `Bill ${docData.number} created`, auth.currentUser, docRef.id, 'Bill');

        if (finalStatus === 'Awaiting Payment' && !isCreditNoteModalOpen && !isRepeatingModalOpen) {
          await postBillJournal(orgId, { ...docData, id: docRef.id }, auth.currentUser.uid);
        }

        if (!isRepeatingModalOpen && !isCreditNoteModalOpen) {
          // Increment next Bill number
          await updateDoc(doc(db, 'settings', orgId), {
            nextBillNumber: increment(1)
          });
        }
      }
      setIsModalOpen(false);
      setIsRepeatingModalOpen(false);
      setIsCreditNoteModalOpen(false);
      setIsEditing(false);
      notify.confirm(isEditing ? "Bill updated" : (finalStatus === 'Awaiting Approval' ? "Bill submitted for approval" : "Bill created"));
    } catch (error) {
      handleFirestoreError(error, isEditing ? OperationType.UPDATE : OperationType.CREATE, 'documents');
      notify.error("Failed to save bill");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedBill || isSaving) {
      return;
    }
    if (!paymentData.accountId) {
      notify.error("Please select an account");
      return;
    }
    if (paymentData.amount <= 0) {
      notify.error("Please enter a valid amount");
      return;
    }

    if (hasLocalSession()) {
      const account = accounts.find(a => a.id === paymentData.accountId) ||
                      bankAccounts.find(a => a.id === paymentData.accountId);
      const accountName = account?.accountName || account?.name || 'Bank';

      setIsSaving(true);
      try {
        const response = await localApiRequest<{
          id: string;
          amount: number;
          paidAmount: number;
          status: Bill['status'];
          journalId: string;
        }>(`/api/organizations/${orgId}/documents/${selectedBill.id}/payments`, {
          method: 'POST',
          body: JSON.stringify({
            amount: paymentData.amount,
            date: paymentData.date,
            accountId: paymentData.accountId,
            accountName,
            method: paymentData.method,
            reference: paymentData.reference,
          }),
        });
        const updatedBill = {
          ...selectedBill,
          paymentReceived: response.paidAmount,
          status: response.status,
        } as Bill;

        setSelectedBill(updatedBill);
        setBills(current => current.map(bill =>
          bill.id === selectedBill.id
            ? { ...bill, paymentReceived: response.paidAmount, status: response.status } as Bill
            : bill
        ));
        setIsPaymentModalOpen(false);
        notify.confirm(response.status === 'Paid' ? "Bill fully paid" : "Partial payment recorded");
      } catch (error) {
        console.error("Error recording local payment:", error);
        notify.error("Failed to record payment");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    setIsSaving(true);
    try {
      console.log("Recording payment for bill:", selectedBill.id);
      
      const currentTotalPaid = selectedBill.paymentReceived || 0;
      const newTotalPaid = currentTotalPaid + paymentData.amount;
      const isFullyPaid = newTotalPaid >= selectedBill.totalAmount;
      const newStatus = isFullyPaid ? 'Paid' : 'Awaiting Payment';

      const account = accounts.find(a => a.id === paymentData.accountId) || 
                      bankAccounts.find(a => a.id === paymentData.accountId);
      const accountName = account?.accountName || account?.name || 'Bank';

      const actionEntry: ActionLogEntry = {
        action: 'Payment Recorded',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: `Payment of ${paymentData.amount.toLocaleString()} recorded via ${accountName}${paymentData.reference ? ` (Ref: ${paymentData.reference})` : ''}`
      };

      await updateDoc(doc(db, 'documents', selectedBill.id), {
        status: newStatus,
        paymentDate: paymentData.date,
        paymentReference: paymentData.reference,
        paymentAccount: paymentData.accountId,
        paymentReceived: newTotalPaid,
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion(actionEntry)
      });

      await logHistory(orgId, 'Recorded Bill Payment', `Payment of ${paymentData.amount.toLocaleString()} recorded for Bill ${selectedBill.number}`, auth.currentUser, selectedBill.id, 'Bill');

      console.log("Bill updated. Creating transaction...");

      // Also create a transaction record
      const transactionData = {
        date: paymentData.date,
        description: `Payment for Bill ${selectedBill.number}`,
        category: 'Expense',
        amount: -paymentData.amount,
        status: 'Completed',
        createdBy: auth.currentUser?.uid,
        createdAt: serverTimestamp(),
        paymentMethod: paymentData.method,
        reference: paymentData.reference,
        contactId: selectedBill.contactId,
        contactName: selectedBill.contactName,
        invoiceId: selectedBill.id,
        accountId: paymentData.accountId,
        orgId
      };
      const paymentDoc = await addDoc(collection(db, 'transactions'), transactionData);

      // Post Payment Journal
      await postPaymentJournal(orgId, { ...transactionData, id: paymentDoc.id }, selectedBill, auth.currentUser?.uid || 'system', 'bill');

      console.log("Transaction created successfully.");

      setSelectedBill({ ...selectedBill, status: newStatus, paymentReceived: newTotalPaid });
      setIsPaymentModalOpen(false);
      notify.confirm(isFullyPaid ? "Bill fully paid" : "Partial payment recorded");
    } catch (error) {
      console.error("Error recording payment:", error);
      handleFirestoreError(error, OperationType.UPDATE, 'documents');
      notify.error("Failed to record payment");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBill = async (id: string) => {
    try {
      if (hasLocalSession()) {
        await localApiRequest(`/api/organizations/${orgId}/documents/${id}`, { method: 'DELETE' });
        setBills(current => current.filter(bill => bill.id !== id));
        setSelectedBill(current => current?.id === id ? null : current);
        notify.confirm("Bill deleted");
        setDeleteId(null);
        return;
      }
      await updateDoc(doc(db, 'documents', id), {
        status: 'Void',
        updatedAt: serverTimestamp()
      });
      notify.confirm("Bill voided");
      setDeleteId(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'documents');
      notify.error("Failed to delete bill");
    }
  };

  const handleRemovePayment = async (payment: any) => {
    if (hasLocalSession()) {
      if (!selectedBill || isSaving) return;
      setIsSaving(true);
      try {
        const response = await localApiRequest<{
          paymentId: string;
          paidAmount: number;
          status: Bill['status'];
          reversalJournalId: string;
        }>(`/api/organizations/${orgId}/documents/${selectedBill.id}/payments/${payment.id}`, {
          method: 'DELETE',
        });
        const updatedBill = {
          ...selectedBill,
          paymentReceived: response.paidAmount,
          status: response.status,
        } as Bill;

        setBillPayments(current => current.filter(currentPayment => currentPayment.id !== response.paymentId));
        setSelectedBill(updatedBill);
        setBills(current => current.map(bill =>
          bill.id === selectedBill.id
            ? { ...bill, paymentReceived: response.paidAmount, status: response.status } as Bill
            : bill
        ));
        setPaymentToDelete(null);
        notify.confirm('Payment removed successfully');
      } catch (error) {
        console.error('Error removing local payment:', error);
        notify.error('Failed to remove payment');
      } finally {
        setIsSaving(false);
      }
      return;
    }
    if (!selectedBill || isSaving) return;
    setIsSaving(true);
    try {
      // Delete the transaction
      await deleteDoc(doc(db, 'transactions', payment.id));

      // Update the bill
      const currentTotalPaid = selectedBill.paymentReceived || 0;
      const newTotalPaid = Math.max(0, currentTotalPaid - Math.abs(payment.amount));
      const newStatus = newTotalPaid === 0 ? 'Awaiting Payment' : 'Awaiting Payment'; // Still awaiting payment if not fully paid

      const actionEntry: ActionLogEntry = {
        action: 'Payment Removed',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: `Payment of ${Math.abs(payment.amount).toLocaleString()} removed. Ref: ${payment.reference}`
      };

      await updateDoc(doc(db, 'documents', selectedBill.id), {
        status: newStatus,
        paymentReceived: newTotalPaid,
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion(actionEntry)
      });

      setSelectedBill({ ...selectedBill, status: newStatus, paymentReceived: newTotalPaid });
      setPaymentToDelete(null);
      notify.confirm("Payment removed successfully");
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'transactions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasLocalSession()) {
      notify.confirm('Bill imports are server-side in local mode.');
      e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results: any) => {
          const data = results.data as any[];
          const newBills: any[] = [];
          
          // Group by InvoiceNumber
          const grouped: Record<string, any[]> = data.reduce((acc: Record<string, any[]>, row: any) => {
            const invNum = row['*InvoiceNumber'];
            if (invNum) {
              if (!acc[invNum]) acc[invNum] = [];
              acc[invNum].push(row);
            }
            return acc;
          }, {});

          for (const [invNum, rows] of Object.entries(grouped)) {
            const firstRow = rows[0];
            const contactName = firstRow['*ContactName'];
            const date = firstRow['*InvoiceDate'] || new Date().toISOString().split('T')[0];
            const dueDate = firstRow['*DueDate'] || date;
            
            const items = (rows as any[]).map((row: any) => ({
              item: '',
              description: row['*Description'] || '',
              quantity: parseFloat(row['*Quantity']) || 1,
              unitPrice: parseFloat(row['*UnitAmount']) || 0,
              discount: 0,
              taxAmount: 0,
              account: row['*AccountCode'] || '',
              taxRate: '0%',
              total: (parseFloat(row['*Quantity']) || 1) * (parseFloat(row['*UnitAmount']) || 0)
            }));

            const totalAmount = items.reduce((sum, item) => sum + item.total, 0);
            
            // Find contact or create a placeholder
            let contact = contacts.find(c => c.name.toLowerCase() === contactName.toLowerCase());
            let contactId = contact?.id || '';

            newBills.push({
              number: invNum,
              contactId,
              contactName,
              contactEmail: contact?.email || '',
              date,
              dueDate,
              reference: '',
              items,
              totalAmount,
              status: 'Draft',
              type: 'bill',
              currency: organizationCurrency,
              brandingTheme: 'Standard',
              amountsAre: 'Tax Exclusive',
              createdBy: auth.currentUser?.uid,
              createdByName: auth.currentUser?.displayName || 'Unknown User',
              createdByEmail: auth.currentUser?.email || 'No Email',
              orgId,
              createdAt: serverTimestamp()
            });
          }

          try {
            for (const bill of newBills) {
              await addDoc(collection(db, 'documents'), bill);
            }
            notify.confirm(`Successfully imported ${newBills.length} bills`);
            setIsImportModalOpen(false);
          } catch (err) {
            console.error(err);
            notify.error("Failed to import bills");
          }
        }
      });
    }
  };

  const handleExportCSV = () => {
    const exportData = filteredBills.flatMap(bill => 
      bill.items.map(item => ({
        '*ContactName': bill.contactName || '',
        '*InvoiceNumber': bill.number,
        '*InvoiceDate': bill.date,
        '*DueDate': bill.dueDate,
        '*Description': item.description,
        '*Quantity': item.quantity,
        '*UnitAmount': item.unitPrice,
        '*AccountCode': item.account,
        'Last Action By': bill.actionLog?.[0]?.user || bill.createdByName || bill.createdBy || 'System'
      }))
    );

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bills_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTemplate = () => {
    const headers = ['*ContactName', '*InvoiceNumber', '*InvoiceDate', '*DueDate', '*Description', '*Quantity', '*UnitAmount', '*AccountCode'];
    const csv = Papa.unparse([headers]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bill_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredBills = bills.filter(bill => {
    const tagMatchesSearch = bill.tags?.some(tagName => tagName.toLowerCase().includes(searchTerm.toLowerCase())) || false;
    const matchesSearch = bill.number.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         bill.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bill.reference.toLowerCase().includes(searchTerm.toLowerCase()) || tagMatchesSearch;
    const matchesStatus = statusFilter === 'All' ? bill.status !== 'Void' : bill.status === statusFilter;
    
    // Advanced search
    const matchesRef = !refFilter || (bill.reference || '').toLowerCase().includes(refFilter.toLowerCase()) || bill.number.toLowerCase().includes(refFilter.toLowerCase());
    const contactName = bill.contactName || '';
    const matchesCustomer = !customerFilter || contactName.toLowerCase().includes(customerFilter.toLowerCase());
    const matchesTag = !tagFilter || (bill.tags && bill.tags.includes(tagFilter));

    // Amount Filters
    const matchesMinAmount = !minAmount || bill.totalAmount >= parseFloat(minAmount);
    const matchesMaxAmount = !maxAmount || bill.totalAmount <= parseFloat(maxAmount);

    // Payment Status Logic
    let paymentStatus = bill.status === 'Paid' ? 'Paid' : (bill.paymentReceived && bill.paymentReceived > 0 ? 'Partially Paid' : 'Unpaid');
    const matchesPaymentStatus = paymentStatusFilter === 'all' || paymentStatus.toLowerCase() === paymentStatusFilter.toLowerCase();

    const matchesStartDate = !startDate || bill.date >= startDate;
    const matchesEndDate = !endDate || bill.date <= endDate;

    return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate && matchesRef && matchesCustomer && matchesTag && matchesMinAmount && matchesMaxAmount && matchesPaymentStatus;
  }).sort((a, b) => {
    if (sortDirection === 'asc') {
      return a.number.localeCompare(b.number);
    } else if (sortDirection === 'desc') {
      return b.number.localeCompare(a.number);
    }
    return 0;
  });

  const activeBillTheme = selectedBill ? brandingThemes.find(t => t.id === selectedBill.brandingTheme) : null;

  const getFilterCount = (status: string) => {
    if (status === 'All') return null;
    return bills.filter(bill => bill.status === status).length;
  };

  return (
    <div className="space-y-6 pt-2 sm:pt-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Bills</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Manage and pay your supplier invoices</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button 
            onClick={() => {
              setIsEditing(false);
              setNewBill({
                number: `BILL-${orgSettings?.nextBillNumber || 1000}`,
                contactId: '',
                contactName: '',
                contactEmail: '',
                date: new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                reference: '',
                currency: organizationCurrency,
                brandingTheme: 'Standard',
                amountsAre: 'Tax Exclusive',
                items: [{ 
                  item: '', 
                  description: '', 
                  quantity: 1, 
                  unitPrice: 0,
                  discount: 0,
                  taxAmount: 0,
                  total: 0,
                  account: '',
                  taxRate: '0%',
                  trackingCategory: ''
                }],
                totalAmount: 0,
                status: 'Draft',
                termsAndConditions: '',
                billingAddress: '',
                deliveryAddress: '',
                type: 'bill',
                trackingCategory: '',
                tags: []
              });
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
          >
            <Plus className="w-4 h-4" />
            <span>New Bill</span>
          </button>

          <button 
            onClick={() => {
              setIsEditing(false);
              setNewBill({
                number: `BILL-${orgSettings?.nextBillNumber || 1000}`,
                contactId: '',
                contactName: '',
                contactEmail: '',
                date: new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                reference: '',
                currency: organizationCurrency,
                brandingTheme: 'Standard',
                amountsAre: 'Tax Exclusive',
                items: [{
                  item: '',
                  description: '',
                  quantity: 1,
                  unitPrice: 0,
                  discount: 0,
                  taxAmount: 0,
                  total: 0,
                  account: '',
                  taxRate: '0%',
                  trackingCategory: ''
                }],
                totalAmount: 0,
                status: 'Draft',
                termsAndConditions: '',
                billingAddress: '',
                deliveryAddress: '',
                type: 'repeating_bill',
                trackingCategory: '',
                tags: []
              });
              setIsRepeatingModalOpen(true);
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-white border border-primary text-primary px-4 py-3 rounded-full hover:bg-primary/5 transition-all font-bold text-sm"
          >
            <Repeat className="w-4 h-4" />
            <span>+ Repeating Bill</span>
          </button>

          <button 
            onClick={() => {
              setIsEditing(false);
              setIsCreditNoteModalOpen(true);
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-white border border-primary text-primary px-4 py-3 rounded-full hover:bg-primary/5 transition-all font-bold text-sm"
          >
            <FileText className="w-4 h-4" />
            <span>+ Credit Note</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div className="relative group/import">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 bg-white border border-primary text-primary px-4 py-3 rounded-full hover:bg-primary/5 transition-all font-bold text-sm"
              >
                <Upload className="w-4 h-4" />
                <span>Import</span>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportCSV}
                accept=".csv"
                className="hidden"
              />
              <div className="absolute left-0 top-full mt-2 w-48 bg-white border border-primary rounded-2xl shadow-xl opacity-0 invisible group-hover/import:opacity-100 group-hover/import:visible transition-all z-50">
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full text-left px-4 py-3 text-sm font-bold hover:bg-primary/5 rounded-t-2xl flex items-center gap-2"
                >
                  <FileUp className="w-4 h-4" />
                  <span>Upload CSV</span>
                </button>
                <button 
                  onClick={downloadTemplate}
                  className="w-full text-left px-4 py-3 text-sm font-bold hover:bg-primary/5 rounded-b-2xl flex items-center gap-2 border-t border-primary/10"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Template</span>
                </button>
              </div>
            </div>
            <button 
              onClick={handleExportCSV}
              className="flex items-center gap-2 bg-white border border-primary text-primary px-4 py-3 rounded-full hover:bg-primary/5 transition-all font-bold text-sm"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col gap-4 bg-white border border-primary/20 p-4 rounded-2xl shadow-sm text-primary">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 w-full lg:w-auto custom-scrollbar">
            {['All', 'Draft', 'Awaiting Approval', 'Awaiting Payment', 'Paid', 'Void'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={cn(
                  "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border flex items-center gap-1.5 shrink-0",
                  statusFilter === status 
                    ? "bg-primary text-primary-foreground border-primary shadow-md" 
                    : "bg-background text-primary border-primary/20 hover:bg-primary/5"
                )}
              >
                <span>{status}</span>
                {getFilterCount(status) !== null && (
                  <span className={cn(
                    "px-1.5 py-0.5 rounded-full text-[10px]",
                    statusFilter === status ? "bg-primary-foreground text-primary" : "bg-primary/10 text-primary"
                  )}>
                    {getFilterCount(status)}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="flex items-center gap-2 bg-background border border-primary/20 px-4 py-2 rounded-full flex-1 md:w-64 shadow-sm focus-within:ring-2 ring-primary/10 transition-all">
                <Search className="w-4 h-4 opacity-50" />
                <input 
                  type="text" 
                  placeholder="Search bills..." 
                  className="w-full bg-transparent border-none outline-none transition-all text-sm font-bold text-primary"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button 
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={cn(
                  "p-2.5 rounded-full border transition-all shadow-sm",
                  showAdvancedFilters || startDate || endDate || minAmount || maxAmount || tagFilter || refFilter || customerFilter || paymentStatusFilter !== 'all'
                    ? "bg-[#141414] text-white border-[#141414]" 
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                )}
                title="Advanced Filters"
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showAdvancedFilters && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-white border border-[#141414]/10 p-6 rounded-3xl shadow-none z-10 animate-in slide-in-from-top-2 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:col-span-4">
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'المرجع / الرقم' : 'Ref / Number'}</label>
              <input 
                type="text" 
                placeholder="PO-001..."
                value={refFilter}
                onChange={e => setRefFilter(e.target.value)}
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'المورد' : 'Vendor/Contact'}</label>
              <input 
                type="text" 
                placeholder={language === 'ar' ? 'البحث بالمورد...' : 'Vendor...'}
                value={customerFilter}
                onChange={e => setCustomerFilter(e.target.value)}
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">Start Date</label>
              <input 
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">End Date</label>
              <input 
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]"
              />
            </div>
            <SearchableSelect 
              label={language === 'ar' ? 'حالة الدفع' : 'Payment Status'}
              value={paymentStatusFilter}
              onChange={e => setPaymentStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Payment Status' },
                { value: 'Unpaid', label: 'Unpaid' },
                { value: 'Partially Paid', label: 'Partially Paid' },
                { value: 'Paid', label: 'Paid' }
              ]}
            />
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-[10px] uppercase font-black opacity-40 tracking-widest text-[#141414]">{language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount'}</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={minAmount} onChange={e => setMinAmount(e.target.value)} placeholder="Min" className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]" />
                <input type="number" value={maxAmount} onChange={e => setMaxAmount(e.target.value)} placeholder="Max" className="w-full bg-white border border-[#141414]/20 rounded-xl px-3 py-2.5 text-xs font-bold outline-none focus:ring-4 ring-[#141414]/5 transition-all text-[#141414]" />
              </div>
            </div>
            <SearchableSelect 
              label={language === 'ar' ? 'الوسوم' : 'Tags'}
              value={tagFilter}
              onChange={e => setTagFilter(e.target.value)}
              options={[
                { value: '', label: 'All Tags' },
                ...availableTags.map((tag: any) => ({ value: tag.name, label: tag.name }))
              ]}
            />
            <div className="md:col-span-4 flex items-end justify-end">
              <button 
                onClick={() => {
                  setRefFilter('');
                  setCustomerFilter('');
                  setTagFilter('');
                  setPaymentStatusFilter('all');
                  setMinAmount('');
                  setMaxAmount('');
                  setStartDate('');
                  setEndDate('');
                  setStatusFilter('All');
                  setSearchTerm('');
                }}
                className="px-6 py-2.5 bg-red-50 text-red-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-colors shadow-sm ml-auto"
              >
                {language === 'ar' ? 'إعادة ضبط الفلاتر' : 'Reset Filters'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedBills.length > 0 && (
        <div className="flex items-center gap-4 bg-primary text-primary-foreground p-4 rounded-2xl mb-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 pr-4 border-r border-white/10">
            <span className="text-xs font-bold italic serif">{selectedBills.length} selected</span>
            <button 
              onClick={() => setSelectedBills([])}
              className="text-[10px] uppercase font-bold opacity-50 hover:opacity-100"
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-2 md:pb-0">
            {statusFilter === 'Draft' && (
              <button 
                onClick={() => handleBulkAction('submit')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <Send className="w-3 h-3" />
                <span>Submit for Approval</span>
              </button>
            )}
            {(statusFilter === 'Draft' || statusFilter === 'Awaiting Approval') && (
              <button 
                onClick={() => handleBulkAction('approve')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <CheckCircle2 className="w-3 h-3" />
                <span>Approve</span>
              </button>
            )}
            {(statusFilter === 'Draft' || statusFilter === 'Awaiting Approval' || statusFilter === 'Awaiting Payment') && (
              <button 
                onClick={() => handleBulkAction('delete')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-red-500/20 text-red-200 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <Trash2 className="w-3 h-3" />
                <span>{statusFilter === 'Awaiting Payment' ? 'Void' : 'Delete'}</span>
              </button>
            )}
            <button 
              onClick={() => handleBulkAction('print')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Printer className="w-3 h-3" />
              <span>Print</span>
            </button>
            <button 
              onClick={() => handleBulkAction('email')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Mail className="w-3 h-3" />
              <span>Email</span>
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading bills...</p>
        </div>
      ) : filteredBills.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-primary/20 rounded-3xl">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No bills found</p>
          <p className="text-sm opacity-30 mt-2">Add your first bill to start tracking payables</p>
        </div>
      ) : (
        <div className="bg-white/50 border border-primary/20 rounded-3xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-primary/20 bg-primary/5">
                <th className="p-4 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-primary/30 text-primary focus:ring-primary/20"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBills(filteredBills.map(b => b.id));
                      } else {
                        setSelectedBills([]);
                      }
                    }}
                    checked={selectedBills.length === filteredBills.length && filteredBills.length > 0}
                  />
                </th>
                <th 
                  className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif cursor-pointer hover:opacity-100 flex items-center gap-1 group"
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                >
                  Number
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                    {sortDirection === 'asc' ? '↑' : (sortDirection === 'desc' ? '↓' : '↕')}
                  </span>
                </th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Ref</th>
                <th className="p-4 w-10"></th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">To</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'الوسوم' : 'Tags'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif text-right">Due</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif"></th>
              </tr>
            </thead>
              <tbody className="divide-y divide-primary/5">
                {filteredBills.map((bill) => {
                  const amountDue = bill.totalAmount - (bill.paymentReceived || 0);
                  return (
                    <tr 
                      key={bill.id} 
                      className="hover:bg-primary/5 transition-colors cursor-pointer group border-b border-primary/10"
                      onClick={() => setSelectedBill(bill)}
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          className="rounded border-primary"
                          checked={selectedBills.includes(bill.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBills(prev => [...prev, bill.id]);
                            } else {
                              setSelectedBills(prev => prev.filter(id => id !== bill.id));
                            }
                          }}
                        />
                      </td>
                      <td className="p-4 font-mono text-sm font-bold">{bill.number}</td>
                      <td className="p-4 text-sm opacity-70 italic serif">{bill.reference || ''}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 opacity-20 group-hover:opacity-100 transition-opacity" />
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowThread(bill.id);
                            }}
                            className="p-1.5 hover:bg-white/20 rounded-full transition-all relative"
                            title="Open Thread"
                          >
                            <MessageSquare className="w-4 h-4" />
                            {bill.threads?.length > 0 && (
                              <span className="text-xs font-bold text-red-500 ml-1">
                                {bill.threads.length}
                              </span>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => {
                            if (onNavigate && (bill.contactId)) {
                              onNavigate('contacts', bill.contactId);
                            }
                          }}
                          className="text-sm font-bold text-left hover:underline hover:text-primary transition-colors"
                        >
                          {(() => {
                            const vendorName = bill.contactName;
                            return typeof vendorName === 'string' ? vendorName : 'Walk-in Customer';
                          })()}
                        </button>
                      </td>
                      <td className="p-4 text-sm opacity-70">{new Date(bill.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td className={cn(
                        "p-4 text-sm font-bold",
                        isOverdue(bill.dueDate, bill.status) ? "text-red-600" : "opacity-70 font-normal"
                      )}>
                        {new Date(bill.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="p-4">
                        {bill.tags && bill.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {(bill.tags || []).map((tagName, idx) => {
                              const tagDetails = availableTags.find((t: any) => t.name === tagName);
                              return (
                                <span 
                                  key={idx} 
                                  className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full text-[10px] font-bold border"
                                  style={{
                                    backgroundColor: tagDetails ? `${tagDetails.color}15` : '#f3f4f6',
                                    color: tagDetails ? tagDetails.color : '#374151',
                                    borderColor: tagDetails ? `${tagDetails.color}30` : '#e5e7eb'
                                  }}
                                >
                                  <div className="p-0.5 rounded-full bg-white/50">
                                    <TagIcon className="w-2.5 h-2.5" />
                                  </div>
                                  {tagName}
                                </span>
                              );
                            })}
                          </div>
                        ) : null}
                      </td>
                      <td className="p-4 font-mono font-bold text-right text-orange-600">
                        {amountDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-4 text-right">
                        <StatusBadge status={billBadgeStatus(bill.status)} language={language === 'ar' ? 'ar' : 'en'} title={bill.status} className="text-[10px] uppercase tracking-wider" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Discussion Thread Modal */}
      {showThread && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowThread(null);
          }}
        >
          <div className="bg-background border border-primary/20 w-full max-w-4xl rounded-[40px] p-8 shadow-2xl relative flex flex-col max-h-[calc(100dvh-2rem)] overflow-hidden">
            <button 
              onClick={() => {
                setShowThread(null);
              }}
              className="absolute top-6 right-6 p-2 hover:bg-primary hover:text-primary-foreground rounded-full transition-colors text-primary z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="mb-4">
              <h3 className="text-2xl font-bold italic serif text-primary">
                {language === 'ar' ? 'خيوط النقاش' : 'Discussion Threads'}
              </h3>
              <p className="text-sm opacity-60 text-primary">
                {language === 'ar' ? 'الفاتورة' : 'Bill'}: {bills.find(b => b.id === showThread)?.number}
              </p>
            </div>

            <div className="flex-1 overflow-hidden">
              <DiscussionsView 
                orgId={orgId} 
                documentId={showThread} 
                collectionName="documents" 
                language={language}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bill Modal */}
      {isModalOpen && (
        <div
          className="app-overlay-inset ui-editor-overlay fixed inset-y-0 end-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-sm z-[150] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsModalOpen(false);
              setIsRepeatingModalOpen(false);
              setIsCreditNoteModalOpen(false);
            }
          }}
        >
          <div className={`${isCreditNoteModalOpen ? 'bg-amber-50' : 'bg-background'} ui-editor-panel w-full max-w-7xl max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col shadow-2xl text-primary`}>
            <div className="ui-editor-header p-6 border-b border-primary/10 flex items-center justify-between bg-primary/5">
              <h2 className="text-2xl font-bold tracking-tight italic serif">
                {isEditing ? `Edit Bill ${newBill.number}` : (isCreditNoteModalOpen ? 'New Credit Note' : (isRepeatingModalOpen ? 'New Repeating Bill' : 'New Bill'))}
              </h2>
              <button 
                onClick={() => {
                  setIsModalOpen(false);
                  setIsRepeatingModalOpen(false);
                  setIsCreditNoteModalOpen(false);
                }}
                className="p-2 hover:bg-primary/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-primary" />
              </button>
            </div>

            <form onSubmit={handleSaveBill} className="ui-editor-form flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              {isRepeatingModalOpen && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-primary/5 p-6 rounded-3xl border border-primary/10">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Repeat every</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        min="1"
                        className="w-20 bg-white border border-primary/20 p-3 rounded-xl outline-none focus:ring-2 focus:ring-primary/10 transition-all font-mono"
                        value={repeatingConfig.frequency}
                        onChange={(e) => setRepeatingConfig({...repeatingConfig, frequency: parseInt(e.target.value)})}
                      />
                      <SearchableSelect 
                        className="p-3 text-sm"
                        value={repeatingConfig.frequencyUnit}
                        onChange={(e) => setRepeatingConfig({...repeatingConfig, frequencyUnit: e.target.value as any})}
                        options={[
                          { value: 'Week', label: 'Week(s)' },
                          { value: 'Month', label: 'Month(s)' }
                        ]}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Next Bill Date</label>
                    <input 
                      type="date" 
                      className="w-full bg-white border border-primary/20 p-3 rounded-xl outline-none focus:border-primary transition-all text-primary font-bold"
                      value={repeatingConfig.startDate}
                      onChange={(e) => setRepeatingConfig({...repeatingConfig, startDate: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        min="0"
                        className="w-20 bg-white border border-primary/20 p-3 rounded-xl outline-none focus:border-primary transition-all font-mono text-primary font-bold"
                        value={repeatingConfig.dueDateDays}
                        onChange={(e) => setRepeatingConfig({...repeatingConfig, dueDateDays: parseInt(e.target.value)})}
                      />
                      <SearchableSelect 
                        className="p-3 text-[10px]"
                        value={repeatingConfig.dueDateType}
                        onChange={(e) => setRepeatingConfig({...repeatingConfig, dueDateType: e.target.value as any})}
                        options={[
                          { value: 'daysAfterBillDate', label: 'day(s) after bill date' },
                          { value: 'dayOfFollowingMonth', label: 'day of following month' },
                          { value: 'dayOfCurrentMonth', label: 'day of current month' },
                          { value: 'daysAfterEndOfBillMonth', label: 'day(s) after end of bill month' }
                        ]}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Approve As</label>
                    <SearchableSelect 
                      className="p-3 text-sm"
                      value={repeatingConfig.approveStatus}
                      onChange={(e) => setRepeatingConfig({...repeatingConfig, approveStatus: e.target.value as any})}
                      options={[
                        { value: 'Draft', label: 'Draft' },
                        { value: 'Approved', label: 'Approved' }
                      ]}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Vendor</label>
                  <SearchableSelect 
                    options={contacts.filter(c => c.type === 'Supplier' || c.type === 'Both').map(c => ({
                      id: c.id,
                      label: c.name,
                      subLabel: c.email
                    }))}
                    value={newBill.contactId}
                    onChange={(e) => handleContactChange(e.target.value)}
                    placeholder="Select a vendor"
                  />
                </div>
                {!isRepeatingModalOpen && (
                  <>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'التاريخ' : 'Date'}</label>
                      <input 
                        required
                        type="date" 
                        className="w-full bg-white/50 border border-primary/20 p-4 rounded-2xl outline-none focus:bg-white focus:border-primary transition-all"
                        value={newBill.date}
                        onChange={(e) => setNewBill({...newBill, date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</label>
                      <input 
                        required
                        type="date" 
                        className="w-full bg-white/50 border border-primary/20 p-4 rounded-2xl outline-none focus:bg-white focus:border-primary transition-all"
                        value={newBill.dueDate}
                        onChange={(e) => setNewBill({...newBill, dueDate: e.target.value})}
                      />
                    </div>
                  </>
                )}
                {isRepeatingModalOpen && (
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">End Date (Optional)</label>
                    <input 
                      type="date" 
                      className="w-full bg-white/50 border border-primary/20 p-4 rounded-2xl outline-none focus:bg-white focus:border-primary transition-all"
                      value={repeatingConfig.endDate}
                      onChange={(e) => setRepeatingConfig({...repeatingConfig, endDate: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {!isRepeatingModalOpen && (
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{isCreditNoteModalOpen ? 'Credit Note Number' : 'Bill Number'}</label>
                    <input 
                      required
                      type="text" 
                      className="w-full bg-white/50 border border-primary/20 p-4 rounded-2xl outline-none focus:bg-white focus:border-primary transition-all font-mono"
                      value={newBill.number}
                      onChange={(e) => setNewBill({...newBill, number: e.target.value})}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                  <input 
                    type="text" 
                    className="w-full bg-white/50 border border-primary/20 p-4 rounded-2xl outline-none focus:bg-white focus:border-primary transition-all"
                    placeholder="e.g. INV-9988"
                    value={newBill.reference}
                    onChange={(e) => setNewBill({...newBill, reference: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'العملة' : 'Currency'}</label>
                  <SearchableSelect 
                    value={newBill.currency}
                    onChange={(e) => setNewBill({...newBill, currency: e.target.value})}
                    options={currencies.map(curr => ({ value: curr, label: curr }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'النسق' : 'Branding Theme'}</label>
                  <SearchableSelect 
                    value={newBill.brandingTheme}
                    onChange={(e) => setNewBill({...newBill, brandingTheme: e.target.value})}
                    options={[
                      { value: 'Standard', label: language === 'ar' ? 'أساسي' : 'Standard' },
                      { value: 'Professional', label: 'Professional' },
                      { value: 'Minimal', label: 'Minimal' },
                      ...brandingThemes.map(theme => ({ value: theme.id, label: theme.name }))
                    ]}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'الوسوم' : 'Tags'}</label>
                <div className="flex gap-2">
                  <SearchableSelect 
                    value={selectedTagToAdd} 
                    onChange={(e) => setSelectedTagToAdd(e.target.value)}
                    options={[
                      { value: '', label: 'Select a tag...' },
                      ...availableTags.filter(t => !newBill.tags?.includes(t.name)).map(tag => ({ value: tag.name, label: tag.name }))
                    ]}
                    className="flex-1"
                  />
                  <button 
                    type="button" 
                    onClick={() => {
                      if (selectedTagToAdd && !newBill.tags?.includes(selectedTagToAdd)) {
                        setNewBill(prev => ({ ...prev, tags: [...(prev.tags || []), selectedTagToAdd] }));
                        setSelectedTagToAdd('');
                      }
                    }}
                    disabled={!selectedTagToAdd}
                    className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold disabled:opacity-50 transition-opacity"
                  >
                    Add
                  </button>
                </div>
                {availableTags.length === 0 && (
                  <p className="text-[10px] opacity-40 italic font-mono uppercase">No tags set up yet.</p>
                )}
                {newBill.tags && newBill.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(newBill.tags || []).map(tagName => {
                      const tagDetails = availableTags.find((t: any) => t.name === tagName);
                      return (
                        <div key={tagName} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border" style={{ borderColor: tagDetails?.color || 'currentColor', backgroundColor: tagDetails?.color ? `${tagDetails.color}20` : 'transparent' }}>
                           <TagIcon className="w-3 h-3" style={{ color: tagDetails?.color || 'currentColor' }} />
                           {tagName}
                           <button type="button" onClick={() => setNewBill(prev => ({ ...prev, tags: prev.tags?.filter(t => t !== tagName) }))} className="ml-1 opacity-50 hover:opacity-100">
                             <X className="w-3 h-3 text-primary" />
                           </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold tracking-tight">{language === 'ar' ? 'عناصر الطلب' : 'Line Items'}</h3>
                  <button 
                    type="button"
                    onClick={handleAddItem}
                    className="flex items-center gap-2 text-xs font-bold hover:opacity-70 transition-opacity"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add New Line</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {(newBill.items || []).map((item, index) => (
                    <div 
                      key={index} 
                      className="grid gap-4 items-start bg-primary/5 p-4 rounded-2xl border border-primary/10"
                      style={{ gridTemplateColumns: `repeat(${12 + trackingCategories.length * 2}, minmax(0, 1fr))` }}
                    >
                      <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">SKU / Item</label>
                        <SearchableSelect 
                          className="bg-transparent border-0 border-b p-0"
                          options={itemsList.map(i => ({
                            id: i.name,
                            label: i.name,
                            subLabel: `SKU: ${i.code || i.sku || 'N/A'}`
                          }))}
                          value={item.item}
                          onChange={(val) => {
                            const selectedItem = itemsList.find(i => i.name === val);
                            if (selectedItem) {
                              handleItemChange(index, {
                                item: selectedItem.name,
                                description: selectedItem.description || '',
                                unitPrice: selectedItem.costPrice || selectedItem.unitPrice || 0,
                                account: selectedItem.account || '',
                                taxRate: selectedItem.taxRate || '0%'
                              });
                            } else {
                              handleItemChange(index, 'item', val);
                            }
                          }}
                          placeholder="Select Item"
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الوصف' : 'Description'}</label>
                        <input 
                          required
                          type="text"
                          placeholder={language === 'ar' ? "الوصف" : "Description"}
                          className="w-full bg-transparent border-b border-primary/20 py-1 outline-none focus:border-primary text-sm text-primary"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الكمية' : 'Qty'}</label>
                        <input 
                          required
                          type="number"
                          className="w-full bg-transparent border-b border-primary/20 py-1 outline-none focus:border-primary text-sm font-mono text-primary"
                          value={isNaN(item.quantity) ? '' : item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">Unit Price</label>
                        <input 
                          required
                          type="number"
                          step="0.01"
                          className="w-full bg-transparent border-b border-primary/20 py-1 outline-none focus:border-primary text-sm font-mono text-primary"
                          value={isNaN(item.unitPrice) ? '' : item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">Discount</label>
                        <div className="flex items-center gap-1">
                          <input 
                            type="number"
                            step="0.01"
                            className="flex-1 bg-transparent border-b border-primary/20 py-1 outline-none focus:border-primary text-xs font-mono text-primary"
                            placeholder="0.00"
                            value={isNaN(item.discount) ? '' : item.discount}
                            onChange={(e) => handleItemChange(index, 'discount', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                          />
                          <SearchableSelect
                            value={item.discountType || 'percentage'}
                            onChange={(e) => handleItemChange(index, 'discountType', e.target.value)}
                            options={[
                              { value: 'percentage', label: '%' },
                              { value: 'amount', label: 'Amt' }
                            ]}
                            className="w-16"
                          />
                        </div>
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الحساب' : 'Account'}</label>
                        <SearchableSelect 
                          className="bg-transparent border-0 border-b p-0"
                          options={accounts.map(acc => ({
                            id: acc.id,
                            label: acc.name,
                            subLabel: acc.code
                          }))}
                          value={item.account}
                          onChange={(val) => handleItemChange(index, 'account', val)}
                          placeholder="Select Account"
                        />
                      </div>
                      <div className="col-span-1 space-y-1">
                        <label className="text-[8px] uppercase font-bold opacity-40">Tax</label>
                        <SearchableSelect 
                          className="bg-transparent border-0 border-b p-0"
                          options={[
                            { id: '0%', label: 'Tax Exempt (0%)' },
                            ...taxRates.filter(t => t.type !== 'sales').map(tax => ({
                              id: tax.name,
                              label: tax.name,
                              subLabel: `${tax.rate}%`
                            }))
                          ]}
                          value={item.taxRate}
                          onChange={(val) => handleItemChange(index, 'taxRate', val)}
                          placeholder="Select Tax"
                        />
                      </div>
                      {trackingCategories.map(cat => (
                        <div key={cat.id} className="col-span-2 space-y-1">
                          <label className="text-[8px] uppercase font-bold opacity-40">{cat.name}</label>
                          <SearchableSelect 
                            className="bg-transparent border-0 border-b p-0"
                            value={item.trackingCategory?.split('|').find(p => p.startsWith(`${cat.id}:`))?.split(':')[1] || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const currentParts = item.trackingCategory ? item.trackingCategory.split('|') : [];
                              const otherParts = currentParts.filter(p => !p.startsWith(`${cat.id}:`));
                              const newParts = val ? [...otherParts, `${cat.id}:${val}`] : otherParts;
                              handleItemChange(index, 'trackingCategory', newParts.join('|'));
                            }}
                            options={[
                              { value: '', label: 'None' },
                              ...cat.options.map((opt: string) => ({ value: opt, label: opt }))
                            ]}
                          />
                        </div>
                      ))}
                      <div className="col-span-1 space-y-1 text-right">
                        <label className="text-[8px] uppercase font-bold opacity-40">{language === 'ar' ? 'الإجمالي' : 'Total'}</label>
                        <div className="py-1 text-sm font-mono font-bold text-primary">
                          {item.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="col-span-1 pt-4 flex justify-end">
                        <button 
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1 hover:text-red-500 transition-colors"
                          disabled={(newBill.items || []).length === 1}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between gap-8 pt-8 border-t border-primary/10">
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}</label>
                    <textarea 
                      className="w-full bg-white border border-primary/20 p-4 rounded-2xl outline-none focus:border-primary transition-all h-24 text-sm text-primary"
                      placeholder="Enter terms and conditions..."
                      value={newBill.termsAndConditions}
                      onChange={(e) => setNewBill({...newBill, termsAndConditions: e.target.value})}
                    />
                  </div>
                </div>
                <div className="w-full md:w-80 space-y-3 font-medium text-primary">
                  <div className="flex justify-between text-sm">
                    <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold">Total (Excl. Disc)</span>
                    <span className="font-mono">{newBill.currency} {newBill.items.reduce((sum, i) => sum + (i.quantity * i.unitPrice), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  {newBill.items.reduce((sum, i) => {
                    const base = i.quantity * i.unitPrice;
                    const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                    return sum + disc;
                  }, 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold text-red-500">Less: Discount</span>
                      <span className="font-mono text-red-500">-{newBill.currency} {newBill.items.reduce((sum, i) => {
                        const base = i.quantity * i.unitPrice;
                        const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                        return sum + disc;
                      }, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t border-primary/10">
                    <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold">{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                    <span className="font-mono">{newBill.currency} {(newBill.items.reduce((sum, i) => {
                      const base = i.quantity * i.unitPrice;
                      const disc = i.discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                      return sum + (base - disc);
                    }, 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="opacity-50 tracking-widest uppercase text-[10px] font-bold">Tax Total</span>
                    <span className="font-mono">{newBill.currency} {newBill.items.reduce((sum, i) => sum + i.taxAmount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-3 border-t border-primary">
                    <span className="italic serif text-primary">{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                    <span className="font-mono text-primary">{newBill.currency} {newBill.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              <div className="ui-editor-actions">
                <button 
                  type="button"
                  data-editor-secondary="true"
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsRepeatingModalOpen(false);
                    setIsCreditNoteModalOpen(false);
                  }}
                  className="px-8 py-4 rounded-full font-bold hover:bg-primary/10 transition-all text-primary"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button 
                  type="button"
                  data-editor-secondary="true"
                  onClick={(e) => handleSaveBill(e as any, 'Awaiting Approval')}
                  disabled={isSaving}
                  className="bg-white border border-primary text-primary px-8 py-4 rounded-full font-bold hover:bg-primary hover:text-primary-foreground transition-all flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>Approve</span>
                </button>
                <button 
                  type="submit"
                  data-editor-primary="true"
                  disabled={isSaving}
                  className="bg-primary text-primary-foreground px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isEditing ? 'Update Bill' : 'Save as Draft'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bill Detail View */}
      {selectedBill && !isModalOpen && (
        <div className="app-overlay-inset fixed inset-y-0 end-0 bg-background z-[100] flex flex-col overflow-hidden print-only print:inset-0">
          {/* Top Header / Breadcrumbs */}
          <div className="bg-background border-b border-primary/10 px-8 py-4 print:hidden">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-[11px] text-primary font-bold">
                  <button onClick={() => setSelectedBill(null)} className="hover:underline tracking-widest uppercase">Purchases</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 opacity-40" />
                  <button onClick={() => setSelectedBill(null)} className="hover:underline tracking-widest uppercase">Bills to pay</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 opacity-40" />
                  <span className="tracking-widest uppercase opacity-60">{selectedBill.number}</span>
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-primary tracking-tight italic serif">Bill {selectedBill.number}</h2>
                  <StatusBadge status={billBadgeStatus(selectedBill.status)} language={language === 'ar' ? 'ar' : 'en'} title={selectedBill.status} className="text-xs uppercase tracking-widest" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                {selectedBill.type === 'repeating_bill' && (
                  <button
                    onClick={() => handleGenerateRecurringBill(selectedBill)}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Repeat className="w-3 h-3" />}
                    Generate Bill
                  </button>
                )}
                {selectedBill.type !== 'repeating_bill' && selectedBill.status === 'Draft' && (
                  <button 
                    onClick={async () => {
                      if (hasLocalSession()) {
                        await updateLocalBillStatus(selectedBill, 'Awaiting Approval');
                      } else {
                        await updateDoc(doc(db, 'documents', selectedBill.id), { status: 'Awaiting Approval', updatedAt: serverTimestamp() });
                        setSelectedBill({ ...selectedBill, status: 'Awaiting Approval' });
                      }
                      notify.confirm("Sent for approval");
                    }}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    Send for Approval
                  </button>
                )}
                {selectedBill.type !== 'repeating_bill' && (selectedBill.status === 'Draft' || selectedBill.status === 'Awaiting Approval') && (
                  <button 
                    onClick={async () => {
                      if (hasLocalSession()) {
                        await updateLocalBillStatus(selectedBill, 'Awaiting Payment');
                      } else {
                        const approvedBill = { ...selectedBill, status: 'Awaiting Payment' as const };
                        await updateDoc(doc(db, 'documents', selectedBill.id), { status: 'Awaiting Payment', updatedAt: serverTimestamp() });
                        // Per SYSTEM_DOCUMENTATION §5.2.1, approving a bill posts the
                        // vendor bill GL journal (Debit expense + VAT input / Credit AP).
                        await postBillJournal(orgId, approvedBill, auth.currentUser?.uid || 'system');
                        await logHistory(orgId, 'Approved Bill', `Bill ${selectedBill.number} approved and journal posted.`, auth.currentUser, selectedBill.id, 'Bill');
                        setSelectedBill(approvedBill);
                      }
                      notify.confirm("Bill approved");
                    }}
                    className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-green-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    Approve
                  </button>
                )}
                {selectedBill.status === 'Awaiting Payment' && (
                  <button 
                    onClick={() => {
                      const amountDue = selectedBill.totalAmount - (selectedBill.paymentReceived || 0);
                      setPaymentData(prev => ({ ...prev, amount: amountDue }));
                      setIsPaymentModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    <CreditCard className="w-3 h-3" />
                    Record Payment
                  </button>
                )}

                {selectedBill.type === 'bill' && ['Approved', 'Billed', 'Draft'].includes(selectedBill.status) && (
                  <button 
                    onClick={() => {
                      setActiveDeliveryNote({
                        invoiceId: selectedBill.id,
                        invoiceNumber: selectedBill.number,
                        contactId: selectedBill.contactId,
                        contactName: selectedBill.contactName,
                        items: (selectedBill.items || []).map(item => ({
                          itemId: item.item, // Actually bill items don't strictly link to catalog id in generic cases, but let's map what we have
                          itemCode: item.item,
                          name: item.item,
                          description: item.description || '',
                          quantityOrdered: item.quantity,
                          quantityShipped: item.receivedQuantity || 0,
                          qtyToShipNow: Math.max(0, item.quantity - (item.receivedQuantity || 0)),
                          lotNumber: '',
                          expiryDate: ''
                        })),
                        assignedPersonnelId: '',
                        assignedPersonnelName: '',
                        warehouseId: '',
                        warehouseName: ''
                      });
                      setIsDeliveryModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-amber-500 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    <Truck className="w-3 h-3" />
                    {language === 'ar' ? 'استلام بضاعة' : 'Receive Goods'}
                  </button>
                )}
                
                <button onClick={() => setShareMode('link')} className="p-2 opacity-40 hover:opacity-100 transition-colors text-primary" title="Get Link"><LinkIcon className="w-5 h-5" /></button>
                <button onClick={() => setShareMode('qr')} className="p-2 opacity-40 hover:opacity-100 transition-colors text-primary" title="QR Code"><QrCode className="w-5 h-5" /></button>
                {!isBillValueLocked(selectedBill.status) && (
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setNewBill({ ...selectedBill });
                      setIsModalOpen(true);
                    }}
                    className="p-2 opacity-40 hover:opacity-100 transition-colors text-primary"
                    title="Edit Status / Content"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                )}
                <button className="opacity-40 hover:opacity-100 text-primary transition-colors hover:scale-110 active:scale-95" onClick={() => handlePrint(selectedBill)}>
                  <Printer className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => window.print()}
                  className="p-2 opacity-40 hover:opacity-100 transition-colors text-primary"
                  title="Download PDF"
                >
                  <Download className="w-5 h-5" />
                </button>

                <button 
                  onClick={() => billFileInputRef.current?.click()}
                  className={cn(
                    "p-2 transition-all flex items-center gap-2 rounded-xl",
                    isUploading ? "bg-primary/10 text-primary" : "opacity-40 hover:opacity-100 hover:bg-primary/5 text-primary"
                  )}
                  title="Attach File"
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-[10px] font-bold">{uploadProgress}%</span>
                    </div>
                  ) : (
                    <Paperclip className="w-5 h-5" />
                  )}
                </button>
                <input 
                  type="file" 
                  ref={billFileInputRef} 
                  onChange={handleFileUpload} 
                  className="hidden" 
                />

                <button 
                  onClick={() => setSelectedBill(null)}
                  className="ml-4 p-1 hover:bg-primary/5 rounded-full transition-colors text-primary"
                >
                  <XCircle className="w-6 h-6 opacity-40 hover:opacity-100" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-8 md:p-16 custom-scrollbar text-primary print:overflow-visible print:p-0 print:h-auto">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Main Card */}
              <div className="bg-background border border-primary/20 rounded-2xl shadow-xl overflow-hidden min-h-[600px]">
                {/* Metadata Grid */}
                <div className="p-8 grid grid-cols-1 md:grid-cols-6 gap-8 bg-primary/5">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold opacity-50 uppercase tracking-widest">Supplier</p>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <p 
                          onClick={() => {
                            if (onNavigate && selectedBill.contactId) {
                               onNavigate('suppliers', selectedBill.contactId);
                            }
                          }}
                          className="text-sm font-bold text-primary hover:underline cursor-pointer italic serif"
                        >
                          {contacts.find(c => c.id === selectedBill.contactId)?.name || selectedBill.contactName || 'Walk-in Contact'}
                        </p>
                        <button 
                          onClick={handleSendEmail}
                          className="p-1 hover:bg-black/5 rounded-full transition-all text-primary/40 hover:text-primary"
                          title="Send via Email"
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="text-[10px] opacity-60 font-medium">
                        <div className="flex items-center gap-2">
                          <p className="text-xs opacity-60 font-bold">
                            {(() => {
                              const contact = contacts.find(c => c.id === selectedBill.contactId) || 
                                             contacts.find(c => c.name === selectedBill.contactName);
                              const email = contact?.email || (contact?.emails && contact.emails.length > 0 ? contact.emails[0] : null) || (selectedBill as any).contactEmail;
                              return email || 'No email provided';
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold opacity-50 uppercase tracking-widest">{language === 'ar' ? 'التاريخ' : 'Date'}</p>
                    <p className="text-sm text-primary font-bold">{new Date(selectedBill.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold opacity-50 uppercase tracking-widest">{language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</p>
                    <p className="text-sm text-primary font-bold">{new Date(selectedBill.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold opacity-50 uppercase tracking-widest">{language === 'ar' ? 'رقم الفاتورة' : 'Bill Number'}</p>
                    <p className="text-sm text-primary font-mono font-bold">{selectedBill.number}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold opacity-50 uppercase tracking-widest">{language === 'ar' ? 'العملة' : 'Currency'}</p>
                    <p className="text-sm text-primary font-bold">{selectedBill.currency}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold opacity-50 uppercase tracking-widest">Theme</p>
                    <p className="text-sm text-primary font-bold">{activeBillTheme?.name || selectedBill.brandingTheme || 'Standard'}</p>
                  </div>
                </div>

                <div className="px-8 py-4 bg-primary/10 flex justify-between items-center">
                  <p className="text-[10px] opacity-40 font-bold uppercase tracking-widest italic">{selectedBill.reference ? `Ref: ${selectedBill.reference}` : ''}</p>
                  
                  {/* Attachments Section */}
                  {selectedBill.attachments && selectedBill.attachments.length > 0 && (
                    <div className="flex items-center gap-2 max-w-[50%] overflow-x-auto no-scrollbar">
                      {selectedBill.attachments.map((file, idx) => (
                        <div key={idx} className="group relative flex items-center gap-1.5 bg-white px-2 py-1 rounded-md border border-primary/10 shadow-sm animate-in fade-in slide-in-from-right-2">
                          <Paperclip className="w-3 h-3 opacity-40" />
                          <a 
                            href={file.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-[10px] font-bold truncate max-w-[120px] hover:text-primary transition-colors cursor-pointer"
                            title={file.name}
                          >
                            {file.name}
                          </a>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAttachment(file.url);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-600 transition-all"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Items Table */}
                <div className="border-t border-primary/10">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-primary/5 border-b border-primary/10">
                        <th className="py-4 px-8 text-[11px] font-bold opacity-50 uppercase tracking-widest">Item</th>
                        <th className="py-4 px-4 text-[11px] font-bold opacity-50 uppercase tracking-widest">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold opacity-50 uppercase tracking-widest text-right">{language === 'ar' ? 'الكمية' : 'Qty'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold opacity-50 uppercase tracking-widest text-right">{language === 'ar' ? 'السعر' : 'Price'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold opacity-50 uppercase tracking-widest text-right">Discount</th>
                        <th className="py-4 px-4 text-[11px] font-bold opacity-50 uppercase tracking-widest text-right">Tax</th>
                        <th className="py-4 px-8 text-[11px] font-bold opacity-50 uppercase tracking-widest text-right">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary/5">
                      {(selectedBill.items || []).map((item, idx) => {
                        const itemData = itemsList.find(i => i.id === item.item || i.code === item.item);
                        return (
                          <tr key={idx} className="hover:bg-primary/5 transition-colors">
                            <td className="py-5 px-8 text-sm font-bold text-primary">
                              <button 
                                onClick={() => {
                                  if (onNavigate && (itemData?.id || item.item)) {
                                    onNavigate('inventory', itemData?.id || item.item);
                                  }
                                }}
                                className="text-left hover:underline hover:text-primary transition-colors focus:outline-none"
                              >
                                {itemData?.code || item.item} {itemData?.name ? `- ${itemData.name}` : ''}
                              </button>
                            </td>
                            <td className="py-5 px-4 text-sm font-bold text-primary italic serif">{item.description}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary text-right">{(item.quantity || 0).toFixed(2)}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary text-right">{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-5 px-4 text-sm font-bold text-primary text-right">
                              {(() => {
                                if (!item.discount) return '-';
                                if ((item as any).discountType === 'amount') {
                                  return `${selectedBill.currency || '$'} ${item.discount.toLocaleString()}`;
                                }
                                return `${item.discount}%`;
                              })()}
                            </td>
                            <td className="py-5 px-4 text-sm font-bold text-primary text-right">{item.taxAmount > 0 ? item.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                            <td className="py-5 px-8 text-sm font-bold text-primary text-right">
                              {(() => {
                                const subLine = item.quantity * item.unitPrice;
                                const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (subLine * (item.discount || 0) / 100);
                                const subtotal = subLine - disc;
                                return (subtotal + (item.taxAmount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Bottom Totals Section */}
                <div className="p-8 flex flex-col md:flex-row justify-between items-start gap-12 border-t border-primary/10 bg-white/10">
                  <div className="space-y-6 flex-1 max-w-sm">
                    {selectedBill.termsAndConditions && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-2">{language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}</p>
                        <p className="text-xs opacity-60 leading-relaxed whitespace-pre-wrap">{selectedBill.termsAndConditions}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="w-80 space-y-4">
                    <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest">
                      <span>Gross Total</span>
                      <span className="font-mono">{selectedBill.currency} {selectedBill.items.reduce((sum, i) => sum + (i.quantity * i.unitPrice), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    {selectedBill.items.reduce((sum, i) => {
                      const base = i.quantity * i.unitPrice;
                      const disc = (i as any).discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                      return sum + disc;
                    }, 0) > 0 && (
                      <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest">
                        <span className="text-red-500">Less: Discount</span>
                        <span className="font-mono text-red-500">-{selectedBill.currency} {selectedBill.items.reduce((sum, i) => {
                          const base = i.quantity * i.unitPrice;
                          const disc = (i as any).discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                          return sum + disc;
                        }, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest pt-2 border-t border-primary/10">
                      <span>{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                      <span className="font-mono">{selectedBill.currency} {(selectedBill.items.reduce((sum, i) => {
                        const base = i.quantity * i.unitPrice;
                        const disc = (i as any).discountType === 'amount' ? (i.discount || 0) : (base * (i.discount || 0) / 100);
                        return sum + (base - disc);
                      }, 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    {selectedBill.items.reduce((sum, i) => sum + (i.taxAmount || 0), 0) > 0 && (
                      <div className="flex justify-between items-center text-xs font-bold text-primary/60 uppercase tracking-widest">
                        <span>{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                        <span className="font-mono">{selectedBill.currency} {selectedBill.items.reduce((sum, i) => sum + (i.taxAmount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    
                    {billPayments.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-primary/10">
                        {billPayments.map((payment, idx) => {
                          const account = accounts.find(a => a.id === payment.accountId) || 
                                          bankAccounts.find(a => a.id === payment.accountId);
                          const accountName = account?.accountName || account?.name || 'Bank/Cash';
                          
                          return (
                            <div key={idx} className="space-y-0.5">
                              <div className="flex justify-between items-center text-xs text-primary">
                                <div className="flex items-center gap-2 group">
                                  <span className="font-bold">{`Less: Payment (${new Date(payment.date).toLocaleDateString()})`}</span>
                                  <button 
                                    onClick={() => setPaymentToDelete(payment)}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-600 transition-all font-mono"
                                  >
                                    [remove]
                                  </button>
                                </div>
                                <span className="font-mono text-right text-emerald-600">- {Math.abs(payment.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                              <div className="text-[10px] text-primary/50 italic font-medium">{accountName}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center text-2xl font-bold pt-4 border-t-2 border-primary">
                      <span className="italic serif">Total Due</span>
                      <span className="font-mono text-orange-600">
                        {selectedBill.currency} {(selectedBill.totalAmount - (selectedBill.paymentReceived || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Log Box */}
              {selectedBill.actionLog && selectedBill.actionLog.length > 0 && (
                <div className="bg-white border border-primary/20 rounded-2xl shadow-xl overflow-hidden mt-8 print:hidden">
                  <div className="flex items-center justify-between border-b border-primary/10 p-6 bg-white/30">
                    <h3 className="text-xl font-bold italic serif">Action Log</h3>
                    <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">
                      {selectedBill.actionLog.length} Event{selectedBill.actionLog.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-primary/10 bg-white/10">
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">Action By</th>
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">Action Type</th>
                          <th className="py-3 px-6 text-[10px] font-bold uppercase tracking-widest opacity-40">Changes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-primary/5">
                        {[...selectedBill.actionLog].reverse().map((entry, idx) => (
                          <tr key={idx} className="text-xs hover:bg-white/40 transition-colors">
                            <td className="py-4 px-6 opacity-60 whitespace-nowrap">{new Date(entry.timestamp).toLocaleString()}</td>
                            <td className="py-4 px-6">
                              <div className="flex flex-col">
                                <span className="font-bold whitespace-nowrap">{entry.user}</span>
                                <span className="opacity-40 text-[10px]">{entry.email}</span>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span className="px-2 py-1 bg-primary/5 rounded-full font-bold uppercase tracking-tighter text-[10px]">
                                {entry.action}
                              </span>
                            </td>
                            <td className="py-4 px-6 opacity-60 leading-relaxed max-w-md">{entry.details}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-8 border-t border-primary/10 print:hidden">
                    <DocumentChatterPanel
                      orgId={orgId}
                      entityId={selectedBill.id}
                      entityType="Bill"
                      locale={language === 'ar' ? 'ar' : 'en'}
                      defaultCollapsed
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Receive Goods / Delivery Note Modal */}
      {isDeliveryModalOpen && activeDeliveryNote && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/20 backdrop-blur-sm z-[250] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsDeliveryModalOpen(false);
              setActiveDeliveryNote(null);
            }
          }}
        >
          <div className="bg-background border border-primary/20 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary text-primary-foreground">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6" />
                <h3 className="text-xl font-bold italic serif">{language === 'ar' ? 'إنشاء إيصال استلام' : 'Create Goods Receipt'}</h3>
              </div>
              <button 
                onClick={() => {
                  setIsDeliveryModalOpen(false);
                  setActiveDeliveryNote(null);
                }}
                className="p-1 hover:bg-white/10 rounded-full transition-all"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] font-bold opacity-40 uppercase tracking-widest leading-none text-primary">{language === 'ar' ? 'الفاتورة' : 'Bill'}</p>
                  <p className="text-sm font-bold text-primary italic serif">{activeDeliveryNote.invoiceNumber}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-bold opacity-40 uppercase tracking-widest leading-none text-primary">{language === 'ar' ? 'المورد' : 'Supplier'}</p>
                  <p className="text-sm font-bold text-primary italic serif">{activeDeliveryNote.contactName}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-primary">{language === 'ar' ? 'الأصناف المستلمة' : 'Items Received'}</h4>
                </div>
                <div className="border border-primary/10 rounded-2xl overflow-hidden shadow-sm bg-white">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-primary/5">
                      <tr>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-primary">{language === 'ar' ? 'الصنف' : 'Item'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-center text-primary">{language === 'ar' ? 'الكمية' : 'Ordered'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-center text-primary">{language === 'ar' ? 'مستلم سابقاً' : 'Received'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-center text-primary">{language === 'ar' ? 'استلام الآن' : 'Receive Now'}</th>
                        <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest opacity-40 text-primary">{language === 'ar' ? 'رقم اللوط / الصلاحية' : 'Lot # / Expiry'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-primary/5">
                      {(activeDeliveryNote.items || []).map((item: any, idx: number) => (
                        <tr key={idx} className="text-primary">
                          <td className="py-3 px-4">
                            <p className="text-xs font-bold leading-tight">{item.itemCode}</p>
                            <p className="text-[10px] opacity-60 truncate max-w-[200px]">{item.description}</p>
                          </td>
                          <td className="py-3 px-4 text-center text-xs font-bold">{item.quantityOrdered}</td>
                          <td className="py-3 px-4 text-center text-xs font-bold text-primary">{item.quantityShipped}</td>
                          <td className="py-3 px-4 text-center">
                            <input 
                              type="number"
                              min="0"
                              max={item.quantityOrdered - item.quantityShipped}
                              value={item.qtyToShipNow}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const maxVal = item.quantityOrdered - item.quantityShipped;
                                const finalVal = Math.min(val, maxVal);
                                
                                const newItems = [...activeDeliveryNote.items];
                                newItems[idx] = { ...item, qtyToShipNow: finalVal };
                                setActiveDeliveryNote({ ...activeDeliveryNote, items: newItems });
                              }}
                              className="w-20 bg-white border border-primary/20 p-1.5 rounded-lg text-center font-bold text-sm focus:ring-2 focus:ring-primary/10 text-primary"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="space-y-1">
                              <input 
                                type="text"
                                placeholder="Lot/Batch No."
                                value={item.lotNumber || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const newItems = [...activeDeliveryNote.items];
                                  const currentItemCode = item.itemId || item.itemCode;
                                  const product = itemsList.find(p => p.id === currentItemCode || p.code === currentItemCode);
                                  let newExpiry = item.expiryDate;
                                  if (product?.lots) {
                                    const matchingLot = product.lots.find((l: any) => l.lotNumber === val);
                                    if (matchingLot?.expiryDate) newExpiry = matchingLot.expiryDate;
                                  }

                                  newItems[idx] = { ...item, lotNumber: val, expiryDate: newExpiry };
                                  setActiveDeliveryNote({ ...activeDeliveryNote, items: newItems });
                                }}
                                className="w-full bg-white border border-primary/20 p-1 rounded font-bold text-[10px] focus:ring-1 focus:ring-primary/10 text-primary outline-none"
                              />
                              <input 
                                type="date"
                                value={item.expiryDate || ''}
                                onChange={(e) => {
                                  const newItems = [...activeDeliveryNote.items];
                                  newItems[idx] = { ...item, expiryDate: e.target.value };
                                  setActiveDeliveryNote({ ...activeDeliveryNote, items: newItems });
                                }}
                                className="w-full bg-white border border-primary/20 p-1 rounded font-bold text-[10px] focus:ring-1 focus:ring-primary/10 text-primary outline-none"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>

            <div className="p-6 border-t border-primary/10 bg-primary/5 flex justify-end gap-3 rounded-b-3xl">
              <button 
                onClick={() => {
                  setIsDeliveryModalOpen(false);
                  setActiveDeliveryNote(null);
                }}
                className="px-6 py-2.5 rounded-xl font-bold hover:bg-primary/5 transition-all text-sm tracking-widest uppercase text-primary border border-transparent hover:border-primary/20"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button 
                onClick={handleSaveDeliveryNote}
                disabled={isSaving}
                className="px-8 py-2.5 rounded-xl font-bold bg-primary text-primary-foreground hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-50 text-sm tracking-widest uppercase"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary-foreground" />
                ) : (
                  <Truck className="w-4 h-4 text-primary-foreground" />
                )}
                <span>{language === 'ar' ? 'حفظ إيصال الاستلام' : 'Save Receipt'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {shareMode && selectedBill && <ShareDocumentModal document={selectedBill} type="bill" mode={shareMode} onClose={() => setShareMode(null)} />}

      {/* Record Payment Modal */}
      {isPaymentModalOpen && selectedBill && (
        <div
          className="fixed inset-0 z-[160] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-primary/20 backdrop-blur-sm shadow-xl"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPaymentModalOpen(false);
          }}
        >
          <div className="bg-background w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-[32px] shadow-2xl border border-primary/20 text-primary">
            <div className="p-6 border-b border-primary/10 flex items-center justify-between bg-primary/5">
                <h2 className="text-xl font-bold italic serif tracking-tight">Record Payment</h2>
                <button 
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="p-2 hover:bg-primary/10 rounded-full transition-all"
                >
                  <X className="w-5 h-5 text-primary" />
                </button>
              </div>
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Date *</label>
                    <input 
                      type="date" 
                      required
                      className="w-full bg-primary/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-primary/20 transition-all font-bold"
                      value={paymentData.date}
                      onChange={(e) => setPaymentData({...paymentData, date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Amount *</label>
                    <input 
                      type="number" 
                      required
                      step="0.01"
                      className="w-full bg-primary/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-primary/20 transition-all font-bold"
                      value={isNaN(paymentData.amount) ? '' : paymentData.amount}
                      onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Paid From *</label>
                  <SearchableSelect 
                    value={paymentData.accountId}
                    onChange={(e) => setPaymentData({...paymentData, accountId: e.target.value})}
                    options={[
                      { value: '', label: 'Select Account' },
                      ...bankAccounts.map(acc => ({ value: acc.id, label: `${acc.accountName} (${acc.accountNumber})`, group: 'Bank Accounts' })),
                      ...accounts.filter(acc => acc.enablePayments).map(acc => ({ value: acc.id, label: `${acc.code} - ${acc.name}`, group: 'Chart of Accounts' }))
                    ]}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Method *</label>
                    <SearchableSelect 
                      value={paymentData.method}
                      onChange={(e) => setPaymentData({...paymentData, method: e.target.value})}
                      options={[
                        { value: 'Bank Transfer', label: 'Bank Transfer' },
                        { value: 'Cash', label: 'Cash' },
                        { value: 'Credit Card', label: 'Credit Card' },
                        { value: 'Check', label: 'Check' },
                        { value: 'Other', label: 'Other' }
                      ]}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                    <input 
                      type="text" 
                      className="w-full bg-primary/5 border border-transparent p-4 rounded-2xl outline-none focus:bg-white focus:border-primary/20 transition-all font-bold"
                      placeholder="e.g. TXN-12345"
                      value={paymentData.reference}
                      onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
                    />
                  </div>
                </div>
              <div className="pt-4 flex items-center gap-3">
                <button 
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="flex-1 px-6 py-3 rounded-2xl font-bold hover:bg-primary/10 transition-all text-primary"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button 
                  onClick={handleRecordPayment}
                  disabled={isSaving}
                  className="flex-1 bg-primary text-primary-foreground px-6 py-3 rounded-2xl font-bold hover:opacity-90 transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin text-primary-foreground" />}
                  <span>Record Payment</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {isImportModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsImportModalOpen(false);
          }}
        >
          <div className="bg-white rounded-[2rem] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain shadow-2xl border border-primary/10">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold">Import Bills</h2>
                <button 
                  onClick={() => setIsImportModalOpen(false)}
                  className="p-2 hover:bg-primary/5 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="p-6 border-2 border-dashed border-primary/20 rounded-2xl text-center">
                  <Upload className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p className="text-sm font-medium mb-4">Select a CSV file to import your bills</p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCSV}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold cursor-pointer"
                  >
                    <FileUp className="w-4 h-4" />
                    <span>Choose File</span>
                  </label>
                </div>

                <div className="bg-white/50 rounded-2xl p-4">
                  <h3 className="text-[10px] font-bold uppercase tracking-wider mb-2 opacity-60">Required Format</h3>
                  <p className="text-[10px] opacity-70 leading-relaxed">
                    Your CSV must include the following columns:<br />
                    <code className="bg-primary/5 px-1 rounded">*ContactName</code>, 
                    <code className="bg-primary/5 px-1 rounded">*InvoiceNumber</code>, 
                    <code className="bg-primary/5 px-1 rounded">*InvoiceDate</code>, 
                    <code className="bg-primary/5 px-1 rounded">*DueDate</code>, 
                    <code className="bg-primary/5 px-1 rounded">{language === 'ar' ? 'الوصف' : 'Description'}</code>, 
                    <code className="bg-primary/5 px-1 rounded">*Quantity</code>, 
                    <code className="bg-primary/5 px-1 rounded">*UnitAmount</code>, 
                    <code className="bg-primary/5 px-1 rounded">*AccountCode</code>
                  </p>
                </div>

                <button
                  onClick={downloadTemplate}
                  className="w-full flex items-center justify-center gap-2 text-sm font-bold hover:underline"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Template CSV</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal 
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDeleteBill(deleteId)}
        title="Delete Bill"
        message="Are you sure you want to delete this bill? This action cannot be undone."
        confirmText="Delete"
      />
      {/* Remove Payment Confirmation Modal */}
      {paymentToDelete && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-primary/80 backdrop-blur-sm z-[250] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPaymentToDelete(null);
          }}
        >
          <div className="bg-white border border-primary w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl shadow-2xl">
            <div className="p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                <Trash2 className="w-10 h-10" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-bold italic serif">Remove Payment?</h3>
                <p className="text-sm opacity-60 leading-relaxed">
                  This will remove the payment of <span className="font-bold text-primary">{selectedBill?.currency} {Math.abs(paymentToDelete.amount).toLocaleString()}</span> recorded on {(() => {
                    const d = new Date(paymentToDelete.date);
                    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                  })()}.
                  The amount due on this bill will increase.
                </p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button 
                  onClick={() => handleRemovePayment(paymentToDelete)}
                  disabled={isSaving}
                  className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Remove and Redo
                </button>
                <button 
                  onClick={() => setPaymentToDelete(null)}
                  disabled={isSaving}
                  className="w-full py-4 bg-white border border-primary text-primary rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-primary/5 transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Print Modal */}
      {isPrintModalOpen && docToPrint && (
        <div className="fixed inset-0 bg-primary/20 backdrop-blur-sm z-[250] flex flex-col items-center overflow-auto p-8 print:p-0 print:bg-white print:block">
          <div className="flex flex-col gap-6 w-full max-w-[850px] print:w-full print:max-w-none">
            <div className="flex items-center justify-between bg-white/80 backdrop-blur-lg p-4 rounded-2xl border border-primary/10 shadow-lg print:hidden">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <Printer className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-tight">Print Preview</h3>
                  <p className="text-[10px] opacity-50 font-bold uppercase tracking-widest">{docToPrint.number}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold text-xs hover:opacity-90 transition-all shadow-md active:scale-95"
                >
                  <Printer className="w-4 h-4" />
                  Print Now
                </button>
                <button 
                  onClick={() => setIsPrintModalOpen(false)}
                  className="p-2.5 hover:bg-red-50 text-red-600 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="bg-white shadow-2xl rounded-sm overflow-hidden print:shadow-none print:rounded-none">
              <DocumentPrintLayout 
                organization={organization}
                document={docToPrint}
                contact={contacts.find(c => c.id === docToPrint.contactId)}
                type="bill"
                language={language}
              />
            </div>
            
            <div className="flex justify-center pb-12 print:hidden">
               <p className="text-[10px] items-center gap-2 flex font-bold uppercase tracking-widest opacity-40">
                  <span className="w-2 h-2 bg-primary/20 rounded-full animate-pulse" />
                  Standard Odoo-Matching Print Layout
               </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
